/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState, useRef, useCallback } from 'react';
import { Mic, Loader, Volume2, MessageCircle, Sparkles, Heart, Brain, ArrowRight, Lock } from 'lucide-react';
import React from 'react';



import NoSleep from 'nosleep.js';



const InviteCodeEntry = ({ onValidCode }) => {
	const [code, setCode] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!code.trim()) {
			setError('Please enter an invite code');
			return;
		}

		setLoading(true);
		setError('');

		try {
			const response = await fetch('/api/natural-matchmaker/validate-invite', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ code })
			});

			const data = await response.json();

			if (data.success) {
				// Store access token
				localStorage.setItem('nm_access_token', data.accessToken);
				localStorage.setItem('nm_access_expires', data.expiresAt);
				onValidCode();
			} else {
				setError(data.error || 'Invalid invite code');
			}
		} catch (error) {
			setError('Failed to validate code. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 flex items-center justify-center p-6">
			<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl p-8 max-w-md w-full border border-white border-opacity-20 shadow-2xl">
				<div className="text-center mb-8">
					<div className="w-20 h-20 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
						<Lock className="text-white" size={32} />
					</div>
					<h2 className="text-3xl font-bold text-white mb-2">Invite Only</h2>
					<p className="text-blue-100">Enter your invite code to access Amy</p>
				</div>

				<form onSubmit={handleSubmit} className="space-y-4">
					<input
						type="text"
						value={code}
						onChange={(e) => setCode(e.target.value.toUpperCase())}
						placeholder="ENTER CODE"
						className="w-full px-4 py-3 bg-white bg-opacity-20 backdrop-blur-lg rounded-xl text-white placeholder-blue-200 border border-white border-opacity-20 focus:outline-none focus:border-white focus:bg-opacity-30 transition-all text-center font-mono text-lg"
						disabled={loading}
						maxLength={12}
					/>
					
					{error && (
						<p className="text-red-300 text-sm text-center">{error}</p>
					)}

					<button
						type="submit"
						disabled={loading}
						className="w-full py-3 bg-white bg-opacity-20 backdrop-blur-lg text-white font-semibold rounded-xl hover:bg-opacity-30 transition-all border border-white border-opacity-20 disabled:opacity-50"
					>
						{loading ? (
							<Loader className="inline animate-spin" size={20} />
						) : (
							'Access Natural Matchmaker'
						)}
					</button>
				</form>

				<p className="text-blue-200 text-xs text-center mt-6">
					Don't have an invite code? Contact support for access.
				</p>
			</div>
		</div>
	);
};

window.GLOBAL_WEBSOCKET = null;
window.ACTIVE_AUDIO_ELEMENTS = new Set();
window.ABORT_EVERYTHING = false;


// Override Audio constructor to track ALL audio elements
const OriginalAudio = window.Audio;
window.Audio = function(...args) {
    const audio = new OriginalAudio(...args);
    window.ACTIVE_AUDIO_ELEMENTS.add(audio);
    
    // Auto-remove when audio ends
    audio.addEventListener('ended', () => {
        window.ACTIVE_AUDIO_ELEMENTS.delete(audio);
    });
    
    // Intercept play to check abort flag
    const originalPlay = audio.play.bind(audio);
    audio.play = function() {
        if (window.ABORT_EVERYTHING) {
            console.log('🚫 BLOCKED audio play - session aborted');
            return Promise.reject(new Error('Session aborted'));
        }
        return originalPlay();
    };
    
    return audio;
};

window.KILL_EVERYTHING_NOW = () => {
    console.log('☠️ NUCLEAR KILL ACTIVATED');
    
    // Set abort flag
    window.ABORT_EVERYTHING = true;
    
    // Kill ALL audio elements we've tracked
    window.ACTIVE_AUDIO_ELEMENTS.forEach(audio => {
        try {
            audio.pause();
            audio.currentTime = 0;
            audio.src = '';
            audio.remove();
        } catch (e) {}
    });
    window.ACTIVE_AUDIO_ELEMENTS.clear();
    
    // Kill the global WebSocket
    if (window.GLOBAL_WEBSOCKET) {
        try {
            window.GLOBAL_WEBSOCKET.onmessage = null;
            window.GLOBAL_WEBSOCKET.onerror = null;
            window.GLOBAL_WEBSOCKET.onclose = null;
            window.GLOBAL_WEBSOCKET.close();
        } catch (e) {}
        window.GLOBAL_WEBSOCKET = null;
    }
    
    // Kill any other audio elements in DOM
    document.querySelectorAll('audio').forEach(a => {
        a.pause();
        a.src = '';
        a.remove();
    });
};

// Helper function to convert Uint8Array to base64
const uint8ArrayToBase64 = (uint8Array) => {
	let binary = '';
	for (let i = 0; i < uint8Array.length; i++) {
		binary += String.fromCharCode(uint8Array[i]);
	}
	return btoa(binary);
};

// Helper function to get auth headers if token exists
const getAuthHeaders = () => {
	const token = localStorage.getItem('authToken');
	const headers = {
		'Content-Type': 'application/json'
	};
	
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}
	
	return headers;
};

class VoiceErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false };
	}
	
	static getDerivedStateFromError(error) {
		return { hasError: true };
	}
	
	componentDidCatch(error, errorInfo) {
		console.error('Voice session error:', error, errorInfo);
	}
	
	render() {
		if (this.state.hasError) {
			return (
				<div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 flex items-center justify-center">
					<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-3xl p-8 max-w-md border border-white border-opacity-20">
						<h2 className="text-2xl font-bold text-white mb-4">Something went wrong</h2>
						<p className="text-blue-100 mb-6">The voice session encountered an error.</p>
						<button
							onClick={() => window.location.reload()}
							className="w-full bg-white bg-opacity-20 text-white font-semibold py-3 px-6 rounded-xl hover:bg-opacity-30 transition-all backdrop-blur-lg"
						>
							Restart Session
						</button>
					</div>
				</div>
			);
		}
		
		return this.props.children;
	}
}

const NaturalMatchmakerPage = () => {

	const [hasAccess, setHasAccess] = useState(false);
	const [checkingAccess, setCheckingAccess] = useState(true);

	const activeMediaStreams = useRef(new Set());
const activeAudioContexts = useRef(new Set());
const activeMediaRecorders = useRef(new Set());
const cleanupMicrophone = useCallback(() => {
    console.log('🎤 Cleaning up ALL microphone resources');
    
    // FIRST: Stop pre-initialized streams
    if (preInitializedStreamRef.current) {
        preInitializedStreamRef.current.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
        });
        preInitializedStreamRef.current = null;
    }
    
    // Stop any active audio stream
    if (audioStream) {
        audioStream.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
        });
        audioStream = null;
    }
    
    // Stop ALL tracked media streams
    activeMediaStreams.current.forEach(stream => {
        stream.getTracks().forEach(track => {
            track.stop();
            track.enabled = false;
        });
    });
    activeMediaStreams.current.clear();
    
    // Close audio contexts
    if (preInitializedAudioContextRef.current && preInitializedAudioContextRef.current.state !== 'closed') {
        preInitializedAudioContextRef.current.close().catch(console.warn);
        preInitializedAudioContextRef.current = null;
    }
    
    if (currentAudioContext && currentAudioContext.state !== 'closed') {
        currentAudioContext.close().catch(console.warn);
        currentAudioContext = null;
    }
    
    // Close ALL audio contexts
    activeAudioContexts.current.forEach(context => {
        if (context.state !== 'closed') {
            context.close().catch(console.warn);
        }
    });
    activeAudioContexts.current.clear();
    
    // Stop ALL media recorders
    activeMediaRecorders.current.forEach(recorder => {
        if (recorder.state === 'recording') {
            recorder.stop();
        }
    });
    activeMediaRecorders.current.clear();
    
    // Stop any active media recorder
    if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
        mediaRecorder = null;
    }
}, []);
	// Check access on mount
	 useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            const token = localStorage.getItem('nm_access_token');
            const expires = localStorage.getItem('nm_access_expires');

            // Check if token exists and not expired
            if (!token || !expires || new Date(expires) < new Date()) {
                setHasAccess(false);
                setCheckingAccess(false);
                return;
            }

            // Validate with backend
            const response = await fetch('/api/natural-matchmaker/check-access', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessToken: token })
            });

            const data = await response.json();
            setHasAccess(data.hasAccess || false);
        } catch (error) {
            console.error('Access check failed:', error);
            setHasAccess(false);
        } finally {
            setCheckingAccess(false);
        }
    };

    // Load available sessions - but ONLY if we have access!
    useEffect(() => {
        // Only check sessions if we have invite code access AND auth token
        if (hasAccess && localStorage.getItem('authToken')) {
            loadAvailableSessions();
        } else if (hasAccess && !localStorage.getItem('authToken')) {
            // Has invite access but not logged in
            setIsCheckingSessions(false);
            setShowSessionChoice(false);
        }
    }, [hasAccess]); // Add hasAccess as dependency


	
	const preInitializedStreamRef = useRef(null);
const preInitializedAudioContextRef = useRef(null);

// Add this function to pre-initialize microphone
const preInitializeMicrophone = useCallback(async () => {
    try {
        console.log('🎤 Pre-initializing microphone...');
        
        // Pre-create audio context
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
        if (audioCtx.state === 'suspended') {
            await audioCtx.resume();
        }
        preInitializedAudioContextRef.current = audioCtx;
        
        // Pre-request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
                sampleRate: { ideal: 24000, min: 16000, max: 48000 },
                channelCount: { exact: 1 },
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                voiceIsolation: true
            }
        });
        
        // Keep the stream alive but muted
        stream.getAudioTracks().forEach(track => {
            track.enabled = false; // Mute it for now
        });
        
        preInitializedStreamRef.current = stream;
        console.log('✅ Microphone pre-initialized and ready');
        
    } catch (err) {
        console.warn('Could not pre-initialize microphone:', err);
    }
}, []);

// Add this to your existing preInitializeAudio effect or create a new one
useEffect(() => {
    if (hasAccess && !checkingAccess) {
        preInitializeMicrophone();
    }
    
    const handlePageUnload = (e) => {
        console.log('🚨 PAGE UNLOADING - EMERGENCY CLEANUP');
        
        // CRITICAL: Stop all tracks synchronously before anything else
        try {
            // Stop pre-initialized streams first
            if (preInitializedStreamRef.current) {
                preInitializedStreamRef.current.getTracks().forEach(track => {
                    track.stop();
                    track.enabled = false;
                });
                preInitializedStreamRef.current = null;
            }
            
            // Stop active audio stream
            if (audioStream) {
                audioStream.getTracks().forEach(track => {
                    track.stop();
                    track.enabled = false;
                });
                audioStream = null;
            }
            
            // Stop ALL tracked media streams
            activeMediaStreams.current.forEach(stream => {
                stream.getTracks().forEach(track => {
                    track.stop();
                    track.enabled = false;
                });
            });
            activeMediaStreams.current.clear();
            
            // Force stop any getUserMedia streams that might be active
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                // This is a nuclear option - query all media devices
                navigator.mediaDevices.enumerateDevices().then(devices => {
                    // Can't directly stop all streams, but we've covered our refs
                }).catch(() => {});
            }
            
            // Close audio contexts
            if (preInitializedAudioContextRef.current) {
                preInitializedAudioContextRef.current.close();
                preInitializedAudioContextRef.current = null;
            }
            
            if (currentAudioContext) {
                currentAudioContext.close();
                currentAudioContext = null;
            }
            
            activeAudioContexts.current.forEach(context => {
                context.close();
            });
            activeAudioContexts.current.clear();
            
            // Kill WebSocket
            if (window.GLOBAL_WEBSOCKET) {
                window.GLOBAL_WEBSOCKET.close();
                window.GLOBAL_WEBSOCKET = null;
            }
            
            // Set abort flag
            window.ABORT_EVERYTHING = true;
            
        } catch (err) {
            console.error('Error in emergency cleanup:', err);
        }
    };
    
    // Add listeners with capture phase for earlier execution
    window.addEventListener('beforeunload', handlePageUnload, true);
    window.addEventListener('unload', handlePageUnload, true);
    window.addEventListener('pagehide', handlePageUnload, true);
    
    // Also add visibilitychange for tab switching
    const handleVisibilityChange = () => {
        if (document.hidden) {
            // Tab is being hidden, might be closing
            handlePageUnload();
        }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
        window.removeEventListener('beforeunload', handlePageUnload, true);
        window.removeEventListener('unload', handlePageUnload, true);
        window.removeEventListener('pagehide', handlePageUnload, true);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        
        // Cleanup on unmount
        cleanupMicrophone();
        
        if (window.GLOBAL_WEBSOCKET) {
            window.GLOBAL_WEBSOCKET.close();
            window.GLOBAL_WEBSOCKET = null;
        }
        
        if (window.preWarmedAudioContext && window.preWarmedAudioContext.state !== 'closed') {
            window.preWarmedAudioContext.close();
        }
        
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
    };
}, [hasAccess, checkingAccess, preInitializeMicrophone, cleanupMicrophone]);
	
	const isSessionStoppingRef = useRef(false);

	const activeListenPromiseRef = useRef(null);
const activeSpeakPromiseRef = useRef(null);

	const [currentQuestionReason, setCurrentQuestionReason] = useState('');


	const [loading, setLoading] = useState(false);
	const [sessionState, setSessionState] = useState('idle'); // 'idle', 'ai-speaking', 'listening', 'analyzing', 'complete'
	const [sessionComplete, setSessionComplete] = useState(false);
	const [matchResult, setMatchResult] = useState(null);
	const [qaHistory, setQaHistory] = useState([]);
	const [statusMessage, setStatusMessage] = useState('');
	const [isFinalQuestion, setIsFinalQuestion] = useState(false);
	const [inferredQuestions, setInferredQuestions] = useState([]);
	const [inferredQAs, setInferredQAs] = useState({});
	const [isVoiceSessionActive, setIsVoiceSessionActive] = useState(false);
const [isCheckingSessions, setIsCheckingSessions] = useState(true);

var noSleep = new NoSleep();

document.addEventListener('click', function enableNoSleep() {
  document.removeEventListener('click', enableNoSleep, false);
  noSleep.enable();
}, false);
	// Audio visualization
	const [userAudioLevels, setUserAudioLevels] = useState(new Array(32).fill(0));
	const [aiAudioLevels, setAiAudioLevels] = useState(new Array(32).fill(0));
	const userAnalyzerRef = useRef(null);
	const aiAnalyzerRef = useRef(null);
	const animationFrameRef = useRef(null);

	let globalSocket = null;
	let mediaRecorder = null;
	let audioStream = null;
	let currentAudioContext = null;

	const [canTriggerMatch, setCanTriggerMatch] = useState(false);
	const [isMatchButtonHovered, setIsMatchButtonHovered] = useState(false);
	const [matchButtonOpacity, setMatchButtonOpacity] = useState(0.3);

	const [hasAnnouncedReady, setHasAnnouncedReady] = useState(false);
	const [isSessionStopping, setIsSessionStopping] = useState(false);
	const [previousInferredCount, setPreviousInferredCount] = useState(0);

	const [currentSessionId, setCurrentSessionId] = useState(null);
	const [showSessionChoice, setShowSessionChoice] = useState(false);
	const [activeSessions, setActiveSessions] = useState([]);
	const [isLoadingSession, setIsLoadingSession] = useState(false);

	const [isSaving, setIsSaving] = useState(false);
const qaHistoryRef = useRef(qaHistory);
const inferredQAsRef = useRef(inferredQAs);
const inferredQuestionsRef = useRef(inferredQuestions);


const lastSavedHashRef = useRef(null);

const saveSessionState = useCallback(async () => {
	if (!localStorage.getItem('authToken')) {
		console.log('No auth token, skipping save');
		return;
	}
	
	// Create a hash of the current state to detect actual changes
	const stateHash = JSON.stringify({
		qaHistory: qaHistoryRef.current,
		inferredQAs: inferredQAsRef.current,
		inferredQuestions: inferredQuestionsRef.current
	});
	
	// Skip if nothing has changed
	if (lastSavedHashRef.current === stateHash) {
		console.log('📌 Skipping save - no actual changes');
		return;
	}
	
	// Skip if we're already saving
	if (isSaving) {
		console.log('📌 Already saving, skipping duplicate save');
		return;
	}
	
	setIsSaving(true);
	lastSavedHashRef.current = stateHash;
	
	// Use a Promise to get the latest state values
	const getLatestState = () => new Promise((resolve) => {
		setQaHistory(currentQaHistory => {
			setInferredQAs(currentInferredQAs => {
				setInferredQuestions(currentInferredQuestions => {
					resolve({
						qaHistory: currentQaHistory,
						inferredQAs: currentInferredQAs,
						inferredQuestions: currentInferredQuestions
					});
					return currentInferredQuestions;
				});
				return currentInferredQAs;
			});
			return currentQaHistory;
		});
	});
	
	try {
		const { qaHistory: latestQaHistory, inferredQAs: latestInferredQAs, inferredQuestions: latestInferredQuestions } = await getLatestState();
		
		console.log('🔵 Saving session state:', {
			sessionId: currentSessionId,
			qaHistoryLength: latestQaHistory.length,
			inferredQAsCount: Object.keys(latestInferredQAs).length
		});
		
		const response = await fetch('/api/natural-matchmaker/save-session', {
			method: 'POST',
			headers: getAuthHeaders(),
			body: JSON.stringify({
				sessionId: currentSessionId,
				previousQA: latestQaHistory,
				inferredQAs: latestInferredQAs,
				inferredQuestions: latestInferredQuestions,
				sessionState,
				hasAnnouncedReady,
				matchButtonOpacity,
				canTriggerMatch,
				previousInferredCount,
				transcript: latestQaHistory.map(qa => ({
					question: qa.question,
					answer: qa.answer,
					questionId: qa.questionId
				})),
				conversationHistory: latestQaHistory,
				currentQuestionReason: currentQuestionReason
			})
		});

		const data = await response.json();
		console.log('🟢 Save response:', data);
		
		if (data.success && data.sessionId && !currentSessionId) {
			console.log('🟢 Setting new session ID:', data.sessionId);
			setCurrentSessionId(data.sessionId);
		}
		
		return data;
	} catch (error) {
		console.error('🔴 Failed to save session:', error);
		throw error;
	} finally {
		setIsSaving(false);
	}
}, [currentSessionId, sessionState, hasAnnouncedReady, matchButtonOpacity, canTriggerMatch, previousInferredCount, currentQuestionReason]);

// Separate effect for other state changes with debounce

useEffect(() => {
    if (!localStorage.getItem('authToken')) return;
    
    // Don't save if we're just loading or have no data
    if (qaHistory.length === 0 && Object.keys(inferredQAs).length === 0 && !currentSessionId) {
        console.log('📌 Skipping save - no session data yet');
        return;
    }
    
    // Only save when we have actual session content
    if (qaHistory.length > 0) {
        console.log('🔵 QA History changed, saving immediately');
        saveSessionState();
    }
}, [qaHistory, saveSessionState]);

useEffect(() => {
    if (!localStorage.getItem('authToken')) return;
    
    // Don't trigger saves for empty sessions
    if (!currentSessionId && qaHistory.length === 0 && Object.keys(inferredQAs).length === 0) {
        return;
    }
    
    const saveDebounced = setTimeout(() => {
        saveSessionState();
    }, 1000);

    return () => clearTimeout(saveDebounced);
}, [inferredQAs, inferredQuestions, sessionState, hasAnnouncedReady, matchButtonOpacity, canTriggerMatch, previousInferredCount, saveSessionState, currentSessionId, qaHistory]);

// Load available sessions on mount for logged-in users
useEffect(() => {
  if (localStorage.getItem('authToken')) {
    loadAvailableSessions();
  } else {
    // Not logged in - immediately set states
    setIsCheckingSessions(false);
    setShowSessionChoice(false);
  }
}, []);

const loadAvailableSessions = async () => {
  setIsCheckingSessions(true);
  try {
    console.log('🔵 Checking for active session...');
    const response = await fetch('/api/natural-matchmaker/sessions', {
      headers: getAuthHeaders()
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('🟢 Session check complete:', data);
    
    if (data.success && data.sessions && data.sessions.length > 0) {
      setActiveSessions([data.sessions[0]]);
      setShowSessionChoice(true);
    } else {
      setShowSessionChoice(false);
    }
  } catch (error) {
    console.error('🔴 Failed to check sessions:', error);
    setShowSessionChoice(false);
  } finally {
    setIsCheckingSessions(false);
  }
};

const startNewSession = () => {
  console.log('🆕 Starting completely new session');
  
  // Mark old sessions as inactive if logged in
   setCurrentSessionId(null);
  setQaHistory([]);
  setInferredQAs({});
  setInferredQuestions([]);
  setHasAnnouncedReady(false);
  setPreviousInferredCount(0);
  setMatchButtonOpacity(0.3);
  setCanTriggerMatch(false);
  setShowSessionChoice(false);
  setIsVoiceSessionActive(false);
  setSessionState('idle');
  
  // Start fresh session
  startSession();
};

const continueSession = async (sessionId) => {
    setIsLoadingSession(true);
    try {
        console.log('🔵 Loading session:', sessionId);
        
        // CRITICAL: Set session ID IMMEDIATELY before any state changes
        // This prevents saveSessionState from creating a new session
        setCurrentSessionId(sessionId);
        
        const response = await fetch(`/api/natural-matchmaker/session/${sessionId}`, {
            headers: getAuthHeaders()
        });
        
        const data = await response.json();
        if (data.success && data.sessionData) {
            console.log('🟢 Session loaded successfully');
            console.log('📊 Session data details:', {
                previousQA: data.sessionData.previousQA,
                previousQA_length: data.sessionData.previousQA?.length || 0,
                inferredQAs_count: Object.keys(data.sessionData.inferredQAs || {}).length
            });
            
            // Load ALL state from the session
            // Backend sends previousQA, not qaHistory
            setQaHistory(data.sessionData.previousQA || data.sessionData.qaHistory || []);
            
            let loadedInferredQAs = data.sessionData.inferredQAs || {};
            let loadedInferredQuestions = data.sessionData.inferredQuestions || [];
            
            // If inferredQuestions is empty but inferredQAs has data, rebuild it
            if (Object.keys(loadedInferredQAs).length > 0 && loadedInferredQuestions.length === 0) {
                console.log('🔧 Rebuilding inferredQuestions from inferredQAs');
                loadedInferredQuestions = Object.keys(loadedInferredQAs);
            }
            
            console.log('📊 LOADED STATE CHECK:', {
                inferredQAs_count: Object.keys(loadedInferredQAs).length,
                inferredQuestions_count: loadedInferredQuestions.length,
                inferredQuestions: loadedInferredQuestions
            });
            
            const inferredCount = Object.keys(loadedInferredQAs).length;
            
            // If at 100% (7 or more insights), set flag for artificial reduction
            const shouldEnableMatch = inferredCount >= 7;

            if (shouldEnableMatch) {
                console.log('🔄 Session already has 7+ insights, enabling match button');
            }

            // Load ALL data as-is
            setInferredQAs(loadedInferredQAs);
            setInferredQuestions(loadedInferredQuestions);

			inferredQAsRef.current = loadedInferredQAs;
			inferredQuestionsRef.current = loadedInferredQuestions;

            // Set match button state based on insight count
            if (shouldEnableMatch) {
                setHasAnnouncedReady(data.sessionData.hasAnnouncedReady || false);
                setMatchButtonOpacity(1); // Full opacity
                setCanTriggerMatch(true); // Enable match button
            } else {
                setHasAnnouncedReady(false);
                setMatchButtonOpacity(0.4 + (inferredCount * 0.1));
                setCanTriggerMatch(false);
            }

            setPreviousInferredCount(data.sessionData.previousInferredCount || 0);
            
            // Load the current question reason if it exists
            if (data.sessionData.currentQuestionReason) {
                setCurrentQuestionReason(data.sessionData.currentQuestionReason);
            }
            
            setShowSessionChoice(false);
            
            // Small delay to ensure React state is fully committed
            setTimeout(() => {
                console.log('🚀 Starting session with loaded data');
                startSession();
            }, 100);
        } else {
            throw new Error('Invalid session data received');
        }
    } catch (error) {
        console.error('Failed to load session:', error);
        alert('Failed to load session. Starting a new one instead.');
        startNewSession();
    } finally {
        setIsLoadingSession(false);
    }
};

useEffect(() => {
    const actualInferredCount = Object.keys(inferredQAs).length;
    // Show reduced count if artificially reduced
    const displayInferredCount = actualInferredCount;
    
    // Trigger animation on change
    if (displayInferredCount > 0) {
        setProgressAnimating(true);
        setTimeout(() => setProgressAnimating(false), 600);
    }
    
    // Progressive opacity based on displayed count
    if (displayInferredCount === 0) {
        setMatchButtonOpacity(0.3);
        setCanTriggerMatch(false);
    } else if (displayInferredCount < 4) {
        setMatchButtonOpacity(0.4 + (displayInferredCount * 0.1));
        setCanTriggerMatch(false);
    } else if (displayInferredCount < 7) {
        setMatchButtonOpacity(0.7 + ((displayInferredCount - 3) * 0.15));
        setCanTriggerMatch(false);
    } else {
        setMatchButtonOpacity(1);
        setCanTriggerMatch(true);
        
        // Announce readiness only once when we hit 7 insights
        // if (!hasAnnouncedReady && isVoiceSessionActive && sessionState !== 'analyzing') {
        //     setHasAnnouncedReady(true);
        //     announceMatchReadiness();
        // }
    }
}, [inferredQAs, hasAnnouncedReady, isVoiceSessionActive, sessionState]);

	const announceMatchReadiness = async () => {
		console.log('🎉 Announcing match readiness to user');
		setSessionState('ai-speaking');
		await speakWithVoiceAPI(
			"Great news! I've learned enough about you to find a wonderful therapist match. You can click the 'Find My Therapist' button whenever you're ready, or we can keep talking if you'd like to share more - it might help me find an even better match for you.",
			'encouraging'
		);
		// Resume normal conversation flow after announcement
		setSessionState('listening');
	};

	const playProxyAudio = async () => {
    if (isSessionStoppingRef.current || sessionState === 'analyzing') {
        console.log('🛑 Skipping proxy audio - session stopped/analyzing');
        return Promise.resolve();
    }

    console.log('🔊 Playing audio from voice proxy');
    setSessionState('ai-speaking');

    return new Promise(async (resolve, reject) => {
        try {
            const socket = getWebSocket();
            
            let audioContext = null;
            let analyser = null;
            let mediaSource = null;
            let sourceBuffer = null;
            let audio = null;
            let pendingChunks = [];
            let isSourceBufferReady = false;
            let isStreamEnded = false;
            let totalBytesAppended = 0;

            // Initialize audio infrastructure
            try {
                audioContext = new (window.AudioContext || window.webkitAudioContext)();
                if (audioContext.state === 'suspended') {
                    await audioContext.resume();
                }
                
                analyser = audioContext.createAnalyser();
                analyser.fftSize = 512;
                analyser.smoothingTimeConstant = 0.8;
                aiAnalyzerRef.current = analyser;
                analyzeAudio(analyser, setAiAudioLevels);

                mediaSource = new MediaSource();
                const url = URL.createObjectURL(mediaSource);
                audio = new Audio(url);
                audio.preload = 'auto';

                const source = audioContext.createMediaElementSource(audio);
                source.connect(analyser);
                source.connect(audioContext.destination);

                await new Promise((resolveSourceOpen) => {
                    mediaSource.addEventListener('sourceopen', () => {
                        console.log('🎵 MediaSource opened for proxy audio');
                        try {
                            mediaSource.duration = 0;
                            sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                            sourceBuffer.mode = 'sequence';
                            isSourceBufferReady = true;

                            sourceBuffer.addEventListener('updateend', () => {
                                if (pendingChunks.length > 0 && !sourceBuffer.updating) {
                                    processAllPendingChunks();
                                } else if (isStreamEnded && pendingChunks.length === 0) {
                                    finalizeStream();
                                }
                            });

                            audio.addEventListener('canplay', () => {
                                if (audio.paused) {
                                    console.log('🎵 Starting proxy audio playback');
                                    audio.play().catch(console.error);
                                }
                            }, { once: true });
                            
                            resolveSourceOpen();
                        } catch (err) {
                            console.error('Error setting up SourceBuffer:', err);
                            resolveSourceOpen();
                        }
                    });
                });
            } catch (err) {
                console.error('Failed to initialize audio:', err);
                reject(err);
                return;
            }

            const messageHandler = async (event) => {
                if (isSessionStoppingRef.current) {
                    socket.removeEventListener('message', messageHandler);
                    cleanup();
                    resolve();
                    return;
                }

                try {
                    const message = JSON.parse(event.data);

                    if (message.type === 'tts-audio-chunk') {
                        const binary = Uint8Array.from(atob(message.data), c => c.charCodeAt(0));
                        pendingChunks.push(binary);
                        
                        if (isSourceBufferReady && sourceBuffer && !sourceBuffer.updating) {
                            processAllPendingChunks();
                        }
                    } else if (message.type === 'tts-audio-complete') {
                        console.log('✅ Proxy TTS streaming completed');
                        isStreamEnded = true;
                        
                        if (isSourceBufferReady && sourceBuffer && !sourceBuffer.updating) {
                            processAllPendingChunks();
                        }
                    }
                } catch (err) {
                    console.error('❌ Failed to parse proxy audio message:', err);
                }
            };

            const processAllPendingChunks = () => {
                if (!isSourceBufferReady || !sourceBuffer || sourceBuffer.updating || pendingChunks.length === 0) {
                    return;
                }

                const MAX_APPEND_SIZE = 128 * 1024;
                let bytesToAppend = 0;
                let chunksToProcess = [];
                
                while (pendingChunks.length > 0 && bytesToAppend < MAX_APPEND_SIZE) {
                    const nextChunk = pendingChunks[0];
                    if (bytesToAppend + nextChunk.length <= MAX_APPEND_SIZE || chunksToProcess.length === 0) {
                        chunksToProcess.push(pendingChunks.shift());
                        bytesToAppend += nextChunk.length;
                    } else {
                        break;
                    }
                }
                
                if (chunksToProcess.length === 0) return;
                
                try {
                    const combinedChunk = new Uint8Array(bytesToAppend);
                    let offset = 0;
                    for (const chunk of chunksToProcess) {
                        combinedChunk.set(chunk, offset);
                        offset += chunk.length;
                    }
                    
                    sourceBuffer.appendBuffer(combinedChunk);
                    totalBytesAppended += bytesToAppend;
                } catch (err) {
                    console.warn('Failed to append buffer:', err);
                    pendingChunks.unshift(...chunksToProcess);
                }
            };

            const finalizeStream = () => {
                if (mediaSource && mediaSource.readyState === 'open' && sourceBuffer && !sourceBuffer.updating) {
                    try {
                        const buffered = sourceBuffer.buffered;
                        if (buffered.length > 0) {
                            const duration = buffered.end(buffered.length - 1);
                            if (mediaSource.readyState === 'open') {
                                mediaSource.duration = duration;
                            }
                        }
                        
                        if (mediaSource.readyState === 'open') {
                            mediaSource.endOfStream();
                        }
                    } catch (err) {
                        console.warn('Error ending stream:', err);
                    }
                }
            };

            const cleanup = () => {
                socket.removeEventListener('message', messageHandler);
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
                setAiAudioLevels(new Array(32).fill(0));
                aiAnalyzerRef.current = null;
            };

            audio.onended = () => {
                console.log('✅ Proxy audio playback completed');
                cleanup();
                resolve();
            };

            audio.onerror = (err) => {
                console.error('❌ Proxy audio playback failed:', err);
                cleanup();
                reject(new Error('Audio playback failed'));
            };

            socket.addEventListener('message', messageHandler);

        } catch (err) {
            console.error('❌ Error in playProxyAudio:', err);
            reject(err);
        }
    });
};

useEffect(() => {
    const handleBeforeUnload = () => {
        cleanupMicrophone();
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
    };
}, [cleanupMicrophone]);
	const [allowFinalSpeech, setAllowFinalSpeech] = useState(false);

const handleTriggerMatch = async () => {
    window.KILL_EVERYTHING_NOW();

	if (!canTriggerMatch || isSessionStoppingRef.current) return;
	setCurrentQuestionReason('');

	console.log('🛑 STOPPING SESSION - User triggered match');
	    cleanupMicrophone();

	// Set all stop flags immediately
	isSessionStoppingRef.current = true;
	setIsSessionStopping(true);  
	setIsVoiceSessionActive(false);
	setSessionState('analyzing');
	setStatusMessage('Finding your perfect therapist match...');

	// FORCE STOP ALL AUDIO ELEMENTS IMMEDIATELY
	const allAudioElements = document.querySelectorAll('audio');
	allAudioElements.forEach(audio => {
		audio.pause();
		audio.currentTime = 0;
		audio.src = '';
		audio.remove();
	});

	// KILL WEBSOCKET WITH EXTREME PREJUDICE
	if (globalSocket) {
		// Remove ALL event listeners first
		globalSocket.onmessage = null;
		globalSocket.onerror = null;
		globalSocket.onclose = null;
		globalSocket.onopen = null;
		
		try {
			globalSocket.close(1000, 'User stopped session');
		} catch (e) {
			// Ignore errors, just kill it
		}
		globalSocket = null;
	}

	// Stop all media streams
	if (mediaRecorder) {
		try {
			mediaRecorder.stop();
		} catch (e) {}
		mediaRecorder = null;
	}

	if (audioStream) {
		audioStream.getTracks().forEach(track => {
			track.stop();
			track.enabled = false;
		});
		audioStream = null;
	}

	// Force close audio context
	if (currentAudioContext) {
		try {
			await currentAudioContext.close();
		} catch (e) {}
		currentAudioContext = null;
	}

	// Cancel animation frames
	if (animationFrameRef.current) {
		cancelAnimationFrame(animationFrameRef.current);
		animationFrameRef.current = null;
	}

	// Reset all audio levels
	setUserAudioLevels(new Array(32).fill(0));
	setAiAudioLevels(new Array(32).fill(0));

	// Wait a bit to ensure everything is dead
	await new Promise(resolve => setTimeout(resolve, 300));
    window.ABORT_EVERYTHING = false;

	 const latestHistory = await new Promise((resolve) => {
        setQaHistory(current => {
            resolve(current);
            return current;
        });
    });

    console.log('📊 Latest history for summary:', {
        length: latestHistory.length,
        history: latestHistory
    });

	// NOW safe to start match process
	await generateSummaryAndMatch(qaHistory);
};

	// Enhanced Circular Audio Visualizer Component
	const CircularVoiceVisualizer = ({ userLevels, aiLevels, isUserActive, isAiActive, sessionState }) => {
		const centerSize = 120;
		const ringWidth = 6;
		const maxRadius = 160;
		const bars = 32;
		
		// Create SVG paths for circular visualization
		const createCircularBars = (levels, radius, isActive, color) => {
			const angleStep = (2 * Math.PI) / bars;
			
			return levels.map((level, i) => {
				const angle = i * angleStep - Math.PI / 2; // Start from top
				const baseRadius = radius;
				const barHeight = isActive ? Math.max(4, level * 40) : 4;
				const innerRadius = baseRadius;
				const outerRadius = baseRadius + barHeight;
				
				// Calculate start and end points for each bar
				const x1 = Math.cos(angle) * innerRadius + 200;
				const y1 = Math.sin(angle) * innerRadius + 200;
				const x2 = Math.cos(angle) * outerRadius + 200;
				const y2 = Math.sin(angle) * outerRadius + 200;
				
				const opacity = isActive ? Math.max(0.4, level * 1.2) : 0.2;
				
				return (
					<line
						key={i}
						x1={x1}
						y1={y1}
						x2={x2}
						y2={y2}
						stroke={color}
						strokeWidth={ringWidth}
						strokeLinecap="round"
						opacity={opacity}
						className="transition-all duration-150"
					/>
				);
			});
		};

		return (
			<div className="relative flex items-center justify-center">
				<svg width="400" height="400" className="transform">
					{/* Center circle with gradient */}
					<defs>
						<radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
    <stop offset="0%" stopColor={
        sessionState === 'preparing' ? '#9333EA' : 
        sessionState === 'ai-speaking' ? '#3B82F6' : 
        sessionState === 'listening' ? '#10B981' : 
        '#6B7280'
    } stopOpacity="0.3" />
    <stop offset="100%" stopColor={
        sessionState === 'preparing' ? '#9333EA' : 
        sessionState === 'ai-speaking' ? '#3B82F6' : 
        sessionState === 'listening' ? '#10B981' : 
        '#6B7280'
    } stopOpacity="0.1" />
</radialGradient>
						<filter id="glow">
							<feGaussianBlur stdDeviation="3" result="coloredBlur"/>
							<feMerge> 
								<feMergeNode in="coloredBlur"/>
								<feMergeNode in="SourceGraphic"/>
							</feMerge>
						</filter>
					</defs>
					
					{/* Background center circle */}
					<circle
						cx="200"
						cy="200"
						r={centerSize / 2}
						fill="url(#centerGradient)"
						className="transition-all duration-500"
					/>
					
					{/* AI voice visualization (inner ring) */}
					{createCircularBars(aiLevels, centerSize / 2 + 20, isAiActive, '#3B82F6')}
					
					{/* User voice visualization (outer ring) */}
					{createCircularBars(userLevels, centerSize / 2 + 80, isUserActive, '#10B981')}
					
{/* Center icon */}
<foreignObject x="170" y="170" width="60" height="60">
    <div className="w-full h-full flex items-center justify-center">
        {sessionState === 'preparing' && (
            <Loader size={28} className="text-white animate-spin" />
        )}
        {sessionState === 'ai-speaking' && (
            <Volume2 size={28} className="text-blue-500 animate-pulse" />
        )}
        {sessionState === 'listening' && (
            <Mic size={28} className="text-green-500 animate-pulse" />
        )}
        {(sessionState === 'analyzing' || sessionState === 'processing') && (
            <Loader size={28} className="text-purple-500 animate-spin" />
        )}
        {sessionState === 'idle' && (
            <MessageCircle size={28} className="text-gray-400" />
        )}
    </div>
</foreignObject>
				</svg>
				
				{/* Legend */}
				<div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex items-center space-x-6 text-sm">
					<div className="flex items-center space-x-2">
						<div className="w-3 h-3 bg-blue-500 rounded-full"></div>
						<span className="text-white text-opacity-80">Amy</span>
					</div>
					<div className="flex items-center space-x-2">
						<div className="w-3 h-3 bg-green-500 rounded-full"></div>
						<span className="text-white text-opacity-80">You</span>
					</div>
				</div>
			</div>
		);
	};

	const cleanupAudio = async () => {
	// Stop all animations first
	if (animationFrameRef.current) {
		cancelAnimationFrame(animationFrameRef.current);
		animationFrameRef.current = null;
	}
	
	// Reset levels immediately
	setUserAudioLevels(new Array(32).fill(0));
	setAiAudioLevels(new Array(32).fill(0));
	
	// Kill all audio contexts
	if (currentAudioContext && currentAudioContext.state !== 'closed') {
		try {
			await currentAudioContext.close();
		} catch (e) {}
		currentAudioContext = null;
	}
	
	// Stop all streams
	if (audioStream) {
		audioStream.getTracks().forEach(t => {
			t.stop();
			t.enabled = false;
		});
		audioStream = null;
	}
	
	// Stop listening if WebSocket is open
	if (globalSocket && globalSocket.readyState === WebSocket.OPEN) {
		try {
			globalSocket.send(JSON.stringify({ type: 'stop-listening' }));
		} catch (e) {}
	}
};

	let finshingdebug = 0;
	// Analyze audio for visualization with better distribution
	const analyzeAudio = (analyser, setLevels) => {
		if (!analyser) return;
		
		const bufferLength = analyser.frequencyBinCount;
		const dataArray = new Uint8Array(bufferLength);
		
		const animate = () => {
			analyser.getByteFrequencyData(dataArray);
			
			// Get overall energy from speech frequencies
			const speechStartBin = Math.floor((80 / 12000) * bufferLength);
			const speechEndBin = Math.floor((3400 / 12000) * bufferLength);
			
			let totalEnergy = 0;
			let count = 0;
			
			for (let i = speechStartBin; i < speechEndBin && i < bufferLength; i++) {
				totalEnergy += dataArray[i];
				count++;
			}
			
			const averageEnergy = count > 0 ? (totalEnergy / count) / 255 : 0;
			const baseLevel = Math.pow(averageEnergy, 0.6);
			
			// Create symmetric pattern around the circle
			const bars = 32;
			const newLevels = [];
			
			for (let i = 0; i < bars; i++) {
				// Create variation using sine waves for smooth, symmetric animation
				const angle = (i / bars) * 2 * Math.PI;
				const wave1 = Math.sin(angle * 2 + Date.now() * 0.003) * 0.3;
				const wave2 = Math.sin(angle * 3 + Date.now() * 0.005) * 0.2;
				const wave3 = Math.sin(angle * 5 + Date.now() * 0.007) * 0.1;
				
				const variation = (wave1 + wave2 + wave3) * baseLevel;
				const level = Math.max(0, Math.min(1, baseLevel + variation + (Math.random() * 0.1 * baseLevel)));
				
				newLevels.push(level);
			}
			
			setLevels(newLevels);
			
			if (animationFrameRef.current) {
				animationFrameRef.current = requestAnimationFrame(animate);
			}
		};
		
		animationFrameRef.current = requestAnimationFrame(animate);
	};

	const getWebSocket = () => {
    if (!window.GLOBAL_WEBSOCKET || window.GLOBAL_WEBSOCKET.readyState !== WebSocket.OPEN) {
        window.GLOBAL_WEBSOCKET = new WebSocket('wss://mindaera.com/ws');//wss://mindaera.com/ws | ws://localhost:8081
        
        window.GLOBAL_WEBSOCKET.onerror = (err) => {
            console.error('❌ WebSocket error:', err);
        };
        
        window.GLOBAL_WEBSOCKET.onclose = () => {
            console.log('WebSocket connection closed');
            window.GLOBAL_WEBSOCKET = null;
        };
    }
    return window.GLOBAL_WEBSOCKET;
};
const preInitializeAudio = useCallback(async () => {
    try {
        // Pre-create audio context
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        if (audioCtx.state === 'suspended') {
            await audioCtx.resume();
        }
        
        // Pre-initialize smoothed levels
        window.smoothedBandLevels = new Array(8).fill(0);
        
        // Store the context for later use
        window.preWarmedAudioContext = audioCtx;
        
        console.log('✅ Audio visualization pre-initialized');
    } catch (err) {
        console.warn('Could not pre-initialize audio:', err);
    }
}, []);

// Add this useEffect to pre-initialize on mount
useEffect(() => {
    // Pre-initialize audio visualization on page load
    preInitializeAudio();
    
    // Also pre-warm the WebSocket connection
    const socket = getWebSocket();
    console.log('🔌 Pre-warming WebSocket connection');
    
    return () => {
        // Cleanup pre-warmed resources if needed
        if (window.preWarmedAudioContext && window.preWarmedAudioContext.state !== 'closed') {
            window.preWarmedAudioContext.close();
        }
    };
}, [preInitializeAudio]);

	let first = true;
	const speakFinal = async (summaryText, recommendation) => {
		// re-enable TTS
		setSkipTTS(false);
		isSessionStoppingRef.current = false;
		// 5a) summary
		await speakWithVoiceAPI(summaryText, 'gentle');

		// 5b) match announcement
		const { name, specialty, matchReasoning } = recommendation;
		const matchText =
			`Based on everything you shared, I've found the perfect therapist for you: ` +
			`${name}, who specializes in ${specialty}. ${matchReasoning}`;
		await speakWithVoiceAPI(matchText, 'enthusiastic');
	};

	const [skipTTS, setSkipTTS] = useState(false);
const speakWithVoiceAPI = async (text, tone = 'calm') => {
    if (isSessionStoppingRef.current || sessionState === 'analyzing') {
        console.log('🛑 Skipping TTS - session stopped/analyzing');
        return Promise.resolve();
    }
    
    if (activeSpeakPromiseRef.current) {
        console.log('🚫 Already speaking, waiting for current speech to complete...');
        try {
            await activeSpeakPromiseRef.current;
        } catch (e) {
            console.error('Previous speak failed:', e);
        }
    }

    if (isSessionStoppingRef.current || sessionState === 'analyzing') {
        console.log('🛑 Skipping TTS - session stopping or analyzing');
        return Promise.resolve();
    }
    
    console.log(`🔊 Speaking: "${text}" with tone: ${tone}`);
    setSessionState('ai-speaking');
    
    // Detect if this is the very first TTS message
    const isFirstMessage = text.includes("How are you feeling today?") || text.includes("I'm Amy");
    
   if(text.includes("Hi, I'm Amy") || text.includes("I'm Amy")) {
    setStatusMessage("Amy is your assistant today");
} else {
    setStatusMessage("I'm thinking about your response...");
}

    // REGULAR STREAMING CODE FOR ALL MESSAGES
    return new Promise(async (resolve, reject) => {
        try {
            const socket = getWebSocket();
            
            if (socket.readyState === WebSocket.CONNECTING) {
                await new Promise((r) => { socket.onopen = r; });
            }

            let audioContext = null;
            let analyser = null;
            let mediaSource = null;
            let sourceBuffer = null;
            let audio = null;
            let pendingChunks = [];
            let isSourceBufferReady = false;
            let isStreamEnded = false;
            let totalBytesAppended = 0;
            let startTime = null;
            let audioInitialized = false;

            // PRE-INITIALIZE AUDIO INFRASTRUCTURE BEFORE SENDING TTS REQUEST
            const initializeAudioEarly = async () => {
                try {
                    // Initialize audio context and visualization FIRST
                    audioContext = new (window.AudioContext || window.webkitAudioContext)();
                    if (audioContext.state === 'suspended') {
                        await audioContext.resume();
                    }
                    
                    analyser = audioContext.createAnalyser();
                    analyser.fftSize = 512;
                    analyser.smoothingTimeConstant = 0.8;
                    aiAnalyzerRef.current = analyser;
                    analyzeAudio(analyser, setAiAudioLevels);

                    // Create MediaSource for streaming
                    mediaSource = new MediaSource();
                    const url = URL.createObjectURL(mediaSource);
                    audio = new Audio(url);
                    audio.preload = 'auto';

                    // Connect to analyzer for visualization
                    const source = audioContext.createMediaElementSource(audio);
                    source.connect(analyser);
                    source.connect(audioContext.destination);

                    // Set up the sourceopen listener
                    await new Promise((resolveSourceOpen) => {
                        mediaSource.addEventListener('sourceopen', () => {
                            console.log('🎵 MediaSource opened');
                            try {
                                mediaSource.duration = 0;
                                
                                sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                                sourceBuffer.mode = 'sequence';
                                
                                isSourceBufferReady = true;
                                audioInitialized = true;

                                sourceBuffer.addEventListener('updateend', () => {
                                    // Continue processing chunks
                                    if (pendingChunks.length > 0 && !sourceBuffer.updating) {
                                        processAllPendingChunks();
                                    } else if (isStreamEnded && pendingChunks.length === 0) {
                                        finalizeStream();
                                    }
                                });

                                sourceBuffer.addEventListener('error', (err) => {
                                    console.error('SourceBuffer error:', err);
                                    fallbackToSimplePlayback();
                                });

                                // Normal playback
                                audio.addEventListener('canplay', () => {
                                    if (audio.paused) {
                                        console.log('🎵 Starting playback');
                                        audio.play().catch(console.error);
                                    }
                                }, { once: true });
                                
                                resolveSourceOpen();
                                
                            } catch (err) {
                                console.error('Error setting up SourceBuffer:', err);
                                fallbackToSimplePlayback();
                                resolveSourceOpen();
                            }
                        });
                    });
                    
                } catch (err) {
                    console.error('Failed to initialize audio early:', err);
                    audioInitialized = false;
                }
            };

            // Initialize audio BEFORE setting up message handler
            await initializeAudioEarly();
            console.log('✅ Audio infrastructure ready, now sending TTS request');
let waitingForAudio = false;

            const messageHandler = async (event) => {
                if (window.ABORT_EVERYTHING || isSessionStoppingRef.current) {
                    console.log('🚫 Message handler aborted');
                    socket.removeEventListener('message', messageHandler);
                    if (typeof cleanup === 'function') cleanup();
                    resolve();
                    return;
                }
                if (isSessionStoppingRef.current) {
                    socket.removeEventListener('message', messageHandler);
                    cleanup();
                    resolve();
                    return;
                }
                try {
                    const message = JSON.parse(event.data);

                    if (message.type === 'tts-audio-start') {
if (!text.includes("Hi, I'm Amy") && !text.includes("I'm here to help")) {
        setStatusMessage('Processing your response...');
    }
	    waitingForAudio = true; // Set flag that we're expecting audio

                        console.log('🎵 TTS streaming started');
                        startTime = Date.now();
                        
                        // Audio should already be initialized, but check
                        if (!audioInitialized) {
                            console.warn('Audio not initialized, initializing now...');
                            await initializeAudioEarly();
                        }

                    } else if (message.type === 'tts-audio-chunk') {
                        const binary = Uint8Array.from(atob(message.data), c => c.charCodeAt(0));
                        if(isSessionStoppingRef.current)
                            return;
                        
                        pendingChunks.push(binary);
                        
                        // Process chunks if buffer is ready
                        if (isSourceBufferReady && sourceBuffer && !sourceBuffer.updating) {
                            if(isSessionStoppingRef.current)
                                return;
                            processAllPendingChunks();
                        }
                    } else if (message.type === 'tts-audio-complete') {
                        console.log('✅ TTS streaming completed');
                        isStreamEnded = true;
                        
                        // Process any remaining chunks
                        if (isSourceBufferReady && sourceBuffer && !sourceBuffer.updating) {
                            processAllPendingChunks();
                        }

                    } else if (message.error) {
                        console.error('❌ TTS error:', message);
                        cleanup();
                        reject(new Error(message.error));
                    }
                } catch (err) {
                    console.error('❌ Failed to parse TTS message:', err);
                    cleanup();
                    reject(err);
                }
            };

            // Rest of your existing functions (processAllPendingChunks, finalizeStream, etc.) remain the same...
            const processAllPendingChunks = () => {
                if (!isSourceBufferReady || !sourceBuffer || sourceBuffer.updating || pendingChunks.length === 0) {
                    return;
                }

                // Combine multiple small chunks for better performance
                const MAX_APPEND_SIZE = 128 * 1024; // 128KB
                let bytesToAppend = 0;
                let chunksToProcess = [];
                
                // Collect chunks up to MAX_APPEND_SIZE
                while (pendingChunks.length > 0 && bytesToAppend < MAX_APPEND_SIZE) {
                    const nextChunk = pendingChunks[0];
                    if (bytesToAppend + nextChunk.length <= MAX_APPEND_SIZE || chunksToProcess.length === 0) {
                        chunksToProcess.push(pendingChunks.shift());
                        bytesToAppend += nextChunk.length;
                    } else {
                        break;
                    }
                }
                
                if (chunksToProcess.length === 0) return;
                
                try {
                    // Combine chunks
                    const combinedChunk = new Uint8Array(bytesToAppend);
                    let offset = 0;
                    for (const chunk of chunksToProcess) {
                        combinedChunk.set(chunk, offset);
                        offset += chunk.length;
                    }
                    
                    console.log(`Appending ${bytesToAppend} bytes (${chunksToProcess.length} chunks combined), ${pendingChunks.length} chunks remaining`);
                    sourceBuffer.appendBuffer(combinedChunk);
                    totalBytesAppended += bytesToAppend;
                    
                    // Update MediaSource duration to prevent premature ending
                    if (mediaSource && mediaSource.readyState === 'open' && !isStreamEnded) {
                        // Estimate duration based on bitrate (adjust based on your audio format)
                        const estimatedBitrate = 128000; // 128kbps for MP3
                        const estimatedDuration = (totalBytesAppended * 8) / estimatedBitrate;
                        
                        try {
                            if (estimatedDuration > mediaSource.duration) {
                                mediaSource.duration = estimatedDuration + 2; // Add 2s buffer
                            }
                        } catch (e) {
                            // Ignore duration update errors
                            console.warn('Could not update duration:', e);
                        }
                    }
                    
                } catch (err) {
                    console.warn('Failed to append buffer:', err);
                    // Put chunks back
                    pendingChunks.unshift(...chunksToProcess);
                    
                    if (err.name === 'QuotaExceededError') {
                        console.log('Quota exceeded, falling back to simple playback');
                        fallbackToSimplePlayback();
                    }
                }
            };

            const finalizeStream = () => {
                if (mediaSource && mediaSource.readyState === 'open' && sourceBuffer && !sourceBuffer.updating) {
                    try {
                        // Set final duration
                        const buffered = sourceBuffer.buffered;
                        if (buffered.length > 0) {
                            const duration = buffered.end(buffered.length - 1);
                            if (mediaSource.readyState === 'open') {
                                mediaSource.duration = duration;
                            }
                        }
                        
                        console.log('Ending MediaSource stream');
                        if (mediaSource.readyState === 'open') {
                            mediaSource.endOfStream();
                        }
                    } catch (err) {
                        console.warn('Error ending stream:', err);
                    }
                }
            };

            const fallbackToSimplePlayback = () => {
                console.log('🎵 Using fallback audio playback');
                cleanup();
                
                // Collect all remaining chunks plus any that were pending
                let allChunks = [...pendingChunks];
                pendingChunks = [];
                
                const fallbackHandler = (event) => {
                    try {
                        const message = JSON.parse(event.data);
                        
                        if (message.type === 'tts-audio-chunk') {
                            const binary = Uint8Array.from(atob(message.data), c => c.charCodeAt(0));
                            allChunks.push(binary);
                        } else if (message.type === 'tts-audio-complete') {
                            // Combine all chunks
                            const totalSize = allChunks.reduce((sum, chunk) => sum + chunk.length, 0);
                            const combinedArray = new Uint8Array(totalSize);
                            let offset = 0;
                            
                            for (const chunk of allChunks) {
                                combinedArray.set(chunk, offset);
                                offset += chunk.length;
                            }

                            const blob = new Blob([combinedArray], { type: 'audio/mpeg' });
                            const url = URL.createObjectURL(blob);
                            const fallbackAudio = new Audio(url);

                            // Re-setup audio context if needed
                            if (audioContext && analyser) {
                                try {
                                    const source = audioContext.createMediaElementSource(fallbackAudio);
                                    source.connect(analyser);
                                    source.connect(audioContext.destination);
                                } catch (e) {
                                    // Might fail if audio context was already used
                                    console.warn('Could not connect fallback audio to analyser');
                                }
                            }

                            fallbackAudio.onended = () => {
                                URL.revokeObjectURL(url);
                                cleanup();
                                resolve();
                            };

                            fallbackAudio.onerror = (err) => {
                                console.error('Fallback audio error:', err);
                                cleanup();
                                reject(err);
                            };

                            fallbackAudio.play().catch(console.error);
                        }
                    } catch (err) {
                        console.error('Fallback handler error:', err);
                    }
                };
                
                socket.removeEventListener('message', messageHandler);
                socket.addEventListener('message', fallbackHandler);
            };

            const cleanup = () => {
                noSleep.disable();
                socket.removeEventListener('message', messageHandler);
                // Fade out before stopping
                if (audio && !audio.paused) {
                    let currentVolume = audio.volume;
                    const fadeOutInterval = setInterval(() => {
                        currentVolume -= 0.1;
                        if (currentVolume <= 0) {
                            audio.volume = 0;
                            clearInterval(fadeOutInterval);
                            // Now safe to clean up
                            cleanupResources();
                        } else {
                            audio.volume = currentVolume;
                        }
                    }, 20);
                } else {
                    cleanupResources();
                }
                
                function cleanupResources() {
                    setIsVoiceSessionActive(false);

                    // Stop AI audio visualization
                    if (animationFrameRef.current) {
                        cancelAnimationFrame(animationFrameRef.current);
                        animationFrameRef.current = null;
                    }
                    setAiAudioLevels(new Array(32).fill(0));
                    aiAnalyzerRef.current = null;
                }
            };

            // Monitor buffer health
            const monitorBuffer = setInterval(() => {
                if (audio && sourceBuffer && !audio.paused) {
                    const buffered = sourceBuffer.buffered;
                    if (buffered.length > 0) {
                        const bufferedEnd = buffered.end(buffered.length - 1);
                        const currentTime = audio.currentTime;
                        const bufferAhead = bufferedEnd - currentTime;
                        
                        console.log(`Buffer health: ${bufferAhead.toFixed(2)}s ahead`);
                        
                        // Check if we're at the end of the stream
                        if (isStreamEnded && pendingChunks.length === 0) {
                            // If buffer is running out and no more data is coming
                            if (bufferAhead < 0.1) {
                                console.log('📍 Stream ended, buffer depleted, stopping monitor');
                                clearInterval(monitorBuffer);
                                
                                // Force the audio to end if it hasn't already
                                if (!audio.ended) {
                                    // Let the remaining buffer play out
                                    setTimeout(() => {
                                        if (!audio.ended && audio.currentTime >= bufferedEnd - 0.05) {
                                            console.log('🛑 Forcing audio end');
                                            audio.pause();
                                            audio.dispatchEvent(new Event('ended'));
                                        }
                                    }, bufferAhead * 1000);
                                }
                                return;
                            }
                        } else if (bufferAhead < 0.5 && pendingChunks.length > 0 && !sourceBuffer.updating) {
                            // Only try to process more chunks if we actually have some
                            console.log('⚠️ Buffer running low, processing more chunks');
                            processAllPendingChunks();
                        }
                    }
                } else if (audio && audio.ended) {
                    // Clean up if audio has already ended
                    console.log('🎯 Audio ended, stopping monitor');
                    clearInterval(monitorBuffer);
                }
            }, 250);

            audio.onended = () => {
                console.log('✅ Audio playback completed');
                clearInterval(monitorBuffer);
                cleanup();
                resolve();
            };

            audio.onerror = (err) => {
                console.error('❌ Audio playback failed:', err);
                clearInterval(monitorBuffer);
                cleanup();
                reject(new Error('Audio playback failed'));
            };
            
            // Set up message handler and send TTS request AFTER audio is ready
            socket.addEventListener('message', messageHandler);
            
            // Small delay to ensure everything is ready
            if (isFirstMessage) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            socket.send(JSON.stringify({
                type: 'tts',
                text: text,
                voice: 'alloy',
                tone: tone
            }));
            
        } catch (err) {
            console.error('❌ Error in speakWithVoiceAPI:', err);
            reject(err);
        }
    });
};
const listenWithVoiceAPI = async () => {
    if (isSessionStoppingRef.current || sessionState === 'analyzing' || sessionState === 'complete') {
        console.log('🛑 Skipping listen - session stopping');
        return { transcript: '', tone: 'neutral' };
    }

    console.log('📞 listenWithVoiceAPI called');
    setSessionState('listening');
    setStatusMessage('Go ahead, I\'m listening...');

    const listenPromise = new Promise(async (resolve, reject) => {
        try {
            const socket = getWebSocket();

            if (socket.readyState === WebSocket.CONNECTING) {
                await new Promise(r => { socket.onopen = r; });
            }

            console.log('✅ WebSocket ready for listening');

            let audioContext, mediaStreamSource, audioStream;
            let isProcessingAudio = true;
            let mediaRecorder = null;
            let hasDetectedSpeech = false;
            let silenceTimer = null;
            let animationFrameId = null;
            
            // Audio playback variables for TTS from proxy
            let ttsAudioContext = null;
            let ttsAnalyser = null;
            let ttsMediaSource = null;
            let ttsSourceBuffer = null;
            let ttsAudio = null;
            let ttsPendingChunks = [];
            let ttsIsSourceBufferReady = false;
            let ttsIsStreamEnded = false;
            let analysisResult = null;
let waitingForAudio = false;

            // VAD parameters
            const SILENCE_THRESHOLD = 0.02;
            const SILENCE_DURATION = 1500;

            const messageHandler = async (event) => {
                const data = JSON.parse(event.data);
                
                if (isSessionStoppingRef.current) {
                    console.log('🛑 Stopping listen - session stopping flag detected');
                    cleanupAudio();
                    resolve({ transcript: '', tone: 'neutral' });
                    return;
                }

                if (data.type === 'session-ready') {
                    console.log('✅ Session initialized');
                } else if (data.type === 'listening-ready') {
    console.log('🎙️ Starting microphone capture...');
    try {
        // Use pre-initialized stream if available
        if (preInitializedStreamRef.current) {
            console.log('✅ Using pre-initialized microphone');
            audioStream = preInitializedStreamRef.current;
            
            // Re-enable the audio tracks
            audioStream.getAudioTracks().forEach(track => {
                track.enabled = true;
            });
            
            // Use pre-initialized audio context
            audioContext = preInitializedAudioContextRef.current || new AudioContext({ sampleRate: 24000 });
            currentAudioContext = audioContext;
            
            // Clear the refs so we don't reuse them
            preInitializedStreamRef.current = null;
            preInitializedAudioContextRef.current = null;
        } else {
            // Fallback to regular initialization
            audioStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: { ideal: 24000, min: 16000, max: 48000 },
                    channelCount: { exact: 1 },
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    voiceIsolation: true
                }
            });

            audioContext = new AudioContext({ sampleRate: 24000 });
            currentAudioContext = audioContext;
        }
        
        if (audioContext.state === 'suspended') {
            await audioContext.resume();
        }

        // Rest of your code remains the same...
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.8;
        userAnalyzerRef.current = analyser;

        mediaStreamSource = audioContext.createMediaStreamSource(audioStream);
        mediaStreamSource.connect(analyser);

                        // Use MediaRecorder with opus codec for OpenAI compatibility
                        mediaRecorder = new MediaRecorder(audioStream, {
                            mimeType: 'audio/webm;codecs=opus',
                            audioBitsPerSecond: 32000
                        });

                        // Stream audio chunks in real-time
                        mediaRecorder.ondataavailable = (event) => {
                            if (event.data.size > 0 && isProcessingAudio) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const base64Audio = reader.result.split(',')[1];
                                    if (socket.readyState === WebSocket.OPEN) {
                                        socket.send(JSON.stringify({
                                            type: 'audio-chunk',
                                            audio: base64Audio
                                        }));
                                    }
                                };
                                reader.readAsDataURL(event.data);
                            }
                        };

                        mediaRecorder.onerror = (event) => {
                            console.error('MediaRecorder error:', event.error);
                        };
						

                        // Your existing VAD analysis code here...
const analyzeAudioWithVAD = () => {
							// if (!isProcessingAudio || !analyser) return;
							 if (!isProcessingAudio || !analyser || !userAnalyzerRef.current) {
        // Stop the animation loop if we're not processing
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
        return;
    }
							const bufferLength = analyser.frequencyBinCount;
							const timeDataArray = new Uint8Array(bufferLength);
							const freqDataArray = new Uint8Array(bufferLength);
							
							// Get both time domain and frequency domain data
							analyser.getByteTimeDomainData(timeDataArray);
							analyser.getByteFrequencyData(freqDataArray);
							
							// 1. Calculate RMS from time domain data
							let sum = 0;
							for (let i = 0; i < timeDataArray.length; i++) {
								const sample = (timeDataArray[i] - 128) / 128;
								sum += sample * sample;
							}
							const rms = Math.sqrt(sum / timeDataArray.length);
							
							// 2. Define frequency bands for speech analysis
							const sampleRate = audioContext.sampleRate;
							const frequencyResolution = sampleRate / 2;
							
							// Critical speech frequency bands
							const fundamentalBand = { low: 80, high: 300 };
							const formantBand = { low: 300, high: 3400 };
							const fricativeBand = { low: 2000, high: 8000 };
							const lowNoiseBand = { low: 20, high: 80 };
							
							const getBandEnergy = (lowFreq, highFreq) => {
								const lowBin = Math.floor((lowFreq / frequencyResolution) * bufferLength);
								const highBin = Math.floor((highFreq / frequencyResolution) * bufferLength);
								let energy = 0;
								for (let i = lowBin; i <= highBin && i < bufferLength; i++) {
									energy += (freqDataArray[i] / 255) ** 2;
								}
								return Math.sqrt(energy / Math.max(1, highBin - lowBin + 1));
							};
							
							const fundamentalEnergy = getBandEnergy(fundamentalBand.low, fundamentalBand.high);
							const formantEnergy = getBandEnergy(formantBand.low, formantBand.high);
							const fricativeEnergy = getBandEnergy(fricativeBand.low, fricativeBand.high);
							const lowNoiseEnergy = getBandEnergy(lowNoiseBand.low, lowNoiseBand.high);
							
							// 3. Calculate spectral features
							
							// Zero Crossing Rate
							let zeroCrossings = 0;
							for (let i = 1; i < timeDataArray.length; i++) {
								if ((timeDataArray[i] - 128) * (timeDataArray[i-1] - 128) < 0) {
									zeroCrossings++;
								}
							}
							const zcr = zeroCrossings / timeDataArray.length;
							
							// Spectral Roll-off
							const totalEnergy = freqDataArray.reduce((sum, val) => sum + (val/255)**2, 0);
							let cumulativeEnergy = 0;
							let rollOffBin = 0;
							for (let i = 0; i < bufferLength; i++) {
								cumulativeEnergy += (freqDataArray[i]/255)**2;
								if (cumulativeEnergy >= 0.85 * totalEnergy) {
									rollOffBin = i;
									break;
								}
							}
							const spectralRollOff = (rollOffBin / bufferLength) * frequencyResolution;
							
							// Spectral Centroid
							let weightedSum = 0;
							let magnitudeSum = 0;
							for (let i = 0; i < bufferLength; i++) {
								const frequency = (i / bufferLength) * frequencyResolution;
								const magnitude = freqDataArray[i] / 255;
								weightedSum += frequency * magnitude;
								magnitudeSum += magnitude;
							}
							const spectralCentroid = magnitudeSum > 0 ? weightedSum / magnitudeSum : 0;
							
							// 4. Speech detection thresholds
							const MIN_RMS = 0.05;
							const MIN_FORMANT_ENERGY = 0.25;
							const MAX_LOW_NOISE_RATIO = 0.4;
							const MIN_ZCR = 0.03;
							const MAX_ZCR = 0.35;
							const MIN_SPECTRAL_CENTROID = 800;
							const MAX_SPECTRAL_CENTROID = 4500;
							const MIN_SPECTRAL_ROLLOFF = 1200;
							const MIN_SPEECH_BANDWIDTH = 0.08;
							
							// 5. Calculate ratios and features
							const lowNoiseRatio = formantEnergy > 0 ? lowNoiseEnergy / formantEnergy : 1;
							const speechBandwidth = formantEnergy + fricativeEnergy;
							const hasFormantStructure = formantEnergy > fundamentalEnergy * 1.2;
							
							// 6. Multi-criteria speech detection
							const conditions = {
								sufficientEnergy: rms > MIN_RMS,
								strongFormants: formantEnergy > MIN_FORMANT_ENERGY,
								lowNoiseRejection: lowNoiseRatio < MAX_LOW_NOISE_RATIO,
								appropriateZCR: zcr > MIN_ZCR && zcr < MAX_ZCR,
								speechlikeCentroid: spectralCentroid > MIN_SPECTRAL_CENTROID && spectralCentroid < MAX_SPECTRAL_CENTROID,
								sufficientBandwidth: speechBandwidth > MIN_SPEECH_BANDWIDTH,
								properRolloff: spectralRollOff > MIN_SPECTRAL_ROLLOFF,
								formantStructure: hasFormantStructure
							};
							
							// Speech detected only if critical conditions are met
							const criticalConditions = [
								conditions.sufficientEnergy,
								conditions.strongFormants,
								conditions.lowNoiseRejection,
								conditions.appropriateZCR,
								conditions.speechlikeCentroid
							];
							
							const isSpeechDetected = criticalConditions.filter(condition => condition).length >= 3;
							
							// 7. Update visualization
							const bars = 32;
							const speechStartBin = Math.floor((80 / frequencyResolution) * bufferLength);
							const speechEndBin = Math.floor((3400 / frequencyResolution) * bufferLength);

							// Sample 8 frequency bands
							const bands = 8;
							const bandLevels = [];
							
							// Initialize smoothed levels if not exists
							if (!window.smoothedBandLevels) {
								window.smoothedBandLevels = new Array(bands).fill(0);
							}

							for (let i = 0; i < bands; i++) {
								const start = speechStartBin + Math.floor((i / bands) * (speechEndBin - speechStartBin));
								const end = speechStartBin + Math.floor(((i + 1) / bands) * (speechEndBin - speechStartBin));
								
								let sum = 0;
								let count = 0;
								
								for (let j = start; j < end && j < bufferLength; j++) {
									sum += freqDataArray[j];
									count++;
								}
								
								const avg = count > 0 ? (sum / count) / 255 : 0;
								const level = Math.pow(avg, 0.5);
								
								// Smooth the levels
								window.smoothedBandLevels[i] += (level - window.smoothedBandLevels[i]) * 0.4;
								bandLevels.push(window.smoothedBandLevels[i]);
							}

							// Create 32 bars with 4-way symmetry
							const newLevels = [];
							const time = Date.now() * 0.0015;

							for (let i = 0; i < bars; i++) {
								const quadrant = Math.floor(i / 8);
								const indexInQuadrant = i % 8;
								
								// Mirror pattern for symmetry
								const bandIndex = quadrant % 2 === 0 ? indexInQuadrant : 7 - indexInQuadrant;
								
								// Base level from frequency band
								let level = bandLevels[bandIndex] * 2.5;
								
								// Add dynamic effects
								const ripple = Math.sin(time * 2.5 - indexInQuadrant * 0.4) * 0.35;
								const pulse = Math.sin(time * 1.8 + bandIndex * 0.5) * 0.3;
								
								// Combine with baseline
								level = level * (1 + ripple + pulse) + 0.15;
								
								// Ensure speech detection shows activity
								if (isSpeechDetected && level < 0.25) {
									level = 0.25;
								}
								
								newLevels.push(Math.max(0, Math.min(1, level)));
							}

							setUserAudioLevels(newLevels);
							
							// 8. VAD logic
							if (isSpeechDetected) {
								if (!hasDetectedSpeech) {
									console.log('🗣️ SPEECH DETECTED:', {
										rms: rms.toFixed(3),
										formantEnergy: formantEnergy.toFixed(3),
										zcr: zcr.toFixed(3),
										centroid: spectralCentroid.toFixed(0) + 'Hz',
										rolloff: spectralRollOff.toFixed(0) + 'Hz',
										conditions: Object.entries(conditions).filter(([_, val]) => val).map(([key, _]) => key)
									});
									hasDetectedSpeech = true;
									setStatusMessage('Listening to you...');
								}
								
								if (silenceTimer) {
									clearTimeout(silenceTimer);
									silenceTimer = null;
								}
							} else {
								if (!silenceTimer && hasDetectedSpeech) {
									silenceTimer = setTimeout(() => {
										console.log('⏰ Silence timeout - stopping recording');
										isProcessingAudio = false;
										 setSessionState('processing');
										setStatusMessage('Processing what you said...');
										// IMMEDIATELY stop the visualization
										if (animationFrameId) {
											cancelAnimationFrame(animationFrameId);
											animationFrameId = null;
										}
										
										// Fade out the user audio levels smoothly
										const fadeOutLevels = () => {
											setUserAudioLevels(currentLevels => {
												const fadedLevels = currentLevels.map(level => level * 0.7);
												const allZero = fadedLevels.every(level => level < 0.01);
												
												if (allZero) {
													return new Array(32).fill(0);
												} else {
													setTimeout(fadeOutLevels, 50);
													return fadedLevels;
												}
											});
										};
										fadeOutLevels();
										
										// Update status to show we're processing
										setStatusMessage('Processing what you said...');
										
										if (mediaRecorder.state === 'recording') {
											mediaRecorder.stop();
										}
										
										setTimeout(() => {
											if (socket.readyState === WebSocket.OPEN) {
												socket.send(JSON.stringify({ type: 'stop-listening' }));
											}
										}, 100);
									}, SILENCE_DURATION);
								}
							}
							
							// if (isProcessingAudio) {
							// 	animationFrameId = requestAnimationFrame(analyzeAudioWithVAD);
							// }
							 if (isProcessingAudio && userAnalyzerRef.current) {
        animationFrameId = requestAnimationFrame(analyzeAudioWithVAD);
    }
						};

                        // Start recording and VAD analysis
                        mediaRecorder.start(100); // 100ms chunks for real-time
                        analyzeAudioWithVAD();

                    } catch (audioError) {
                        console.error('❌ Microphone access error:', audioError);
                        // ... (keep your existing error handling)
                    }

                } else if (data.type === 'tts-audio-start') {
					    waitingForAudio = true; // Set flag that we're expecting audio

                    console.log('🔊 TTS audio starting from voice proxy');
                    setSessionState('ai-speaking');
                    setStatusMessage('Amy is responding...');
                    
                    // Initialize TTS audio playback
                    try {
                        ttsAudioContext = new (window.AudioContext || window.webkitAudioContext)();
                        if (ttsAudioContext.state === 'suspended') {
                            await ttsAudioContext.resume();
                        }
                        
                        ttsAnalyser = ttsAudioContext.createAnalyser();
                        ttsAnalyser.fftSize = 512;
                        ttsAnalyser.smoothingTimeConstant = 0.8;
                        aiAnalyzerRef.current = ttsAnalyser;
                        analyzeAudio(ttsAnalyser, setAiAudioLevels);

                        ttsMediaSource = new MediaSource();
                        const url = URL.createObjectURL(ttsMediaSource);
                        ttsAudio = new Audio(url);
                        ttsAudio.preload = 'auto';

                        const source = ttsAudioContext.createMediaElementSource(ttsAudio);
                        source.connect(ttsAnalyser);
                        source.connect(ttsAudioContext.destination);

                        await new Promise((resolveSourceOpen) => {
                            ttsMediaSource.addEventListener('sourceopen', () => {
                                console.log('🎵 MediaSource opened for TTS');
                                try {
                                    ttsMediaSource.duration = 0;
                                    ttsSourceBuffer = ttsMediaSource.addSourceBuffer('audio/mpeg');
                                    ttsSourceBuffer.mode = 'sequence';
                                    ttsIsSourceBufferReady = true;

                                    ttsSourceBuffer.addEventListener('updateend', () => {
                                        if (ttsPendingChunks.length > 0 && !ttsSourceBuffer.updating) {
                                            processTTSChunks();
                                        } else if (ttsIsStreamEnded && ttsPendingChunks.length === 0) {
                                            finalizeTTSStream();
                                        }
                                    });

                                    ttsAudio.addEventListener('canplay', () => {
                                        if (ttsAudio.paused) {
                                            console.log('🎵 Starting TTS playback');
                                            ttsAudio.play().catch(console.error);
                                        }
                                    }, { once: true });
                                    
                                    resolveSourceOpen();
                                } catch (err) {
                                    console.error('Error setting up TTS SourceBuffer:', err);
                                    resolveSourceOpen();
                                }
                            });
                        });
                        
                        // Set up audio ended handler
                        ttsAudio.onended = () => {
        console.log('✅ TTS playback completed');
        cleanupTTSAudio();
        
        // Check if we already have the analysis result
        if (analysisResult) {
            console.log('📊 Analysis already complete, resolving now');
            cleanupAudio();
            socket.removeEventListener('message', messageHandler);
            resolve(analysisResult);
        } else {
            console.log('⏳ Audio ended but waiting for analysis...');
            waitingForAudio = false; // Mark that audio is done
        }
    };
                        
                    } catch (err) {
                        console.error('Failed to initialize TTS audio:', err);
                    }
                    
                } else if (data.type === 'tts-audio-chunk') {
                    const binary = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
                    ttsPendingChunks.push(binary);
                    
                    if (ttsIsSourceBufferReady && ttsSourceBuffer && !ttsSourceBuffer.updating) {
                        processTTSChunks();
                    }
                    
                } else if (data.type === 'tts-audio-complete') {
                    console.log('✅ TTS streaming completed from proxy');
                    ttsIsStreamEnded = true;
                    
                    if (ttsIsSourceBufferReady && ttsSourceBuffer && !ttsSourceBuffer.updating) {
                        processTTSChunks();
                    }
                    
                } else if (data.type === 'analysis-complete') {
    console.log('📝 Analysis complete:', data);
    
    // Store the analysis result
    analysisResult = {
        transcript: data.transcript.text,
        tone: data.emotion,
        therapeuticResponse: data.therapeuticResponse,
        inference: data.inference,
        questionsForAnalysis: data.questionsForAnalysis,
        ttsHandledByProxy: true
    };
    
    // Check if we should resolve immediately
    if (!waitingForAudio || !ttsAudio) {
        // Either we're not waiting for audio, or audio setup failed
        console.log('🚀 No audio to wait for, resolving immediately');
        cleanupAudio();
        socket.removeEventListener('message', messageHandler);
        resolve(analysisResult);
    } else if (ttsAudio && ttsAudio.ended) {
        // Audio already finished playing
        console.log('🎯 Audio already ended, resolving now');
        cleanupAudio();
        socket.removeEventListener('message', messageHandler);
        resolve(analysisResult);
    }
    // Otherwise, wait for audio to complete
    else {
        console.log('🎵 Waiting for audio to complete before resolving...');
    }
} else if (data.type === 'session-complete') {
                    console.log('🏁 Session complete');
                    setSessionState('complete');
                    cleanupAudio();
                    cleanupTTSAudio();
                    socket.removeEventListener('message', messageHandler);
                    resolve({ 
                        sessionComplete: true, 
                        message: data.message 
                    });
                    
                } else if (data.error) {
                    console.error('❌ Error from proxy:', data.error);
                    cleanupAudio();
                    cleanupTTSAudio();
                    socket.removeEventListener('message', messageHandler);
                    
                    // ... (keep your existing error handling)
                }
            };

            const processTTSChunks = () => {
                if (!ttsIsSourceBufferReady || !ttsSourceBuffer || ttsSourceBuffer.updating || ttsPendingChunks.length === 0) {
                    return;
                }

                const MAX_APPEND_SIZE = 128 * 1024;
                let bytesToAppend = 0;
                let chunksToProcess = [];
                
                while (ttsPendingChunks.length > 0 && bytesToAppend < MAX_APPEND_SIZE) {
                    const nextChunk = ttsPendingChunks[0];
                    if (bytesToAppend + nextChunk.length <= MAX_APPEND_SIZE || chunksToProcess.length === 0) {
                        chunksToProcess.push(ttsPendingChunks.shift());
                        bytesToAppend += nextChunk.length;
                    } else {
                        break;
                    }
                }
                
                if (chunksToProcess.length === 0) return;
                
                try {
                    const combinedChunk = new Uint8Array(bytesToAppend);
                    let offset = 0;
                    for (const chunk of chunksToProcess) {
                        combinedChunk.set(chunk, offset);
                        offset += chunk.length;
                    }
                    
                    ttsSourceBuffer.appendBuffer(combinedChunk);
                } catch (err) {
                    console.warn('Failed to append TTS buffer:', err);
                    ttsPendingChunks.unshift(...chunksToProcess);
                }
            };

            const finalizeTTSStream = () => {
                if (ttsMediaSource && ttsMediaSource.readyState === 'open' && ttsSourceBuffer && !ttsSourceBuffer.updating) {
                    try {
                        const buffered = ttsSourceBuffer.buffered;
                        if (buffered.length > 0) {
                            const duration = buffered.end(buffered.length - 1);
                            if (ttsMediaSource.readyState === 'open') {
                                ttsMediaSource.duration = duration;
                            }
                        }
                        
                        if (ttsMediaSource.readyState === 'open') {
                            ttsMediaSource.endOfStream();
                        }
                    } catch (err) {
                        console.warn('Error ending TTS stream:', err);
                    }
                }
            };

            const cleanupTTSAudio = () => {
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                    animationFrameRef.current = null;
                }
                setAiAudioLevels(new Array(32).fill(0));
                aiAnalyzerRef.current = null;
                
                if (ttsAudioContext && ttsAudioContext.state !== 'closed') {
                    ttsAudioContext.close().catch(console.warn);
                }
            };

            const cleanupAudio = () => {
                 isProcessingAudio = false;
    
    if (silenceTimer) {
        clearTimeout(silenceTimer);
        silenceTimer = null;
    }
    
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }
    
    if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
    }
    
    // Ensure user levels are zeroed
    setUserAudioLevels(new Array(32).fill(0));
    userAnalyzerRef.current = null;
                
                if (mediaRecorder && mediaRecorder.state === 'recording') {
                    mediaRecorder.stop();
                }
                
                if (mediaStreamSource) {
                    mediaStreamSource.disconnect();
                }
                
                if (audioContext && audioContext.state !== 'closed') {
                    audioContext.close().catch(console.warn);
                }
                
                if (audioStream) {
                    audioStream.getTracks().forEach(track => track.stop());
                }
                
                setUserAudioLevels(new Array(32).fill(0));
                userAnalyzerRef.current = null;
                
                // Clean up smoothed levels
                if (window.smoothedBandLevels) {
                    delete window.smoothedBandLevels;
                }
            };

            socket.addEventListener('message', messageHandler);

            // Initialize session first
            socket.send(JSON.stringify({
                type: 'init-session',
                userId: localStorage.getItem('authToken'),
                previousQA: qaHistory,
                inferredQuestionIds: inferredQuestions
            }));

            // Wait for session ready
            await new Promise((resolveSession) => {
                const sessionHandler = (event) => {
                    const data = JSON.parse(event.data);
                    if (data.type === 'session-ready') {
                        console.log('✅ Session ready, starting listening');
                        resolveSession();
                    }
                };
                socket.addEventListener('message', sessionHandler);
                
                // Timeout for session ready
                setTimeout(() => {
                    socket.removeEventListener('message', sessionHandler);
                    resolveSession();
                }, 5000);
            });

            // Now start listening
            socket.send(JSON.stringify({ type: 'start-listening' }));

        } catch (err) {
            console.error('❌ listenWithVoiceAPI failed:', err);
            reject(err);
        }
    });

    activeListenPromiseRef.current = listenPromise;
    return listenPromise;
};

const generateSummaryAndMatch = async (finalHistory) => {
    setSessionState('analyzing');
    setStatusMessage('Creating your personalized recommendations...');
    setIsVoiceSessionActive(false);
        cleanupMicrophone();

    // Get current inferred Q&As
    const getCurrentInferredQAs = () => {
        return new Promise((resolve) => {
            setInferredQAs(current => {
                resolve(current);
                return current;
            });
        });
    };

    const currentInferredQAs = await getCurrentInferredQAs();
    
    // If qaHistory is empty but we have inferred QAs, construct a history from them
    let historyForSummary = finalHistory;
    if (finalHistory.length === 0 && Object.keys(currentInferredQAs).length > 0) {
        console.log('📝 No explicit Q&A history, constructing from inferred QAs');
        
        // Create a pseudo-history from inferred QAs
        historyForSummary = Object.entries(currentInferredQAs).map(([questionId, data]) => ({
            questionId: questionId,
            question: questionId, // Use the question ID as the question text
            answer: data.fullTranscript || data.reasoning || data.answer
        }));
    }
    
    console.log('🔍 FRONTEND - Sending to finalize:', {
        explicit_QAs: finalHistory.length,
        inferred_QAs: Object.keys(currentInferredQAs).length,
        inferredQAs: currentInferredQAs,
        historyForSummary_length: historyForSummary.length
    });

    // Start the matching process in the background
    const matchPromise = fetch('/api/natural-matchmaker/finalize', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
            previousQA: finalHistory,
            inferredQAs: currentInferredQAs
        })
    });

    // Generate a summary based on the conversation
    console.log('📝 Sending to generate-summary:', {
        previousQA: historyForSummary,
        length: historyForSummary.length
    });

    const summaryRes = await fetch('/api/natural-matchmaker/generate-summary', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
            previousQA: historyForSummary
        })
    });
    // Add better error handling
    if (!summaryRes.ok) {
        console.error('Summary generation failed:', summaryRes.status);
        const errorText = await summaryRes.text();
        console.error('Error details:', errorText);
    } else {
        const summaryData = await summaryRes.json();
        
        if (summaryData.success) {
            // Speak the fun, insightful summary
            isSessionStoppingRef.current = false;
            await speakWithVoiceAPI(summaryData.summary, 'warm');
        }
    }

    // Wait for matching to complete
    const matchRes = await matchPromise;
    const matchData = await matchRes.json();

    if (matchData.success) {
        setMatchResult(matchData.recommendation);
        isSessionStoppingRef.current = false;

        await speakWithVoiceAPI(
            `Based on everything you've shared, I've found the perfect therapist for you. ${matchData.recommendation.name} specializes in ${matchData.recommendation.specialty}. ${matchData.recommendation.matchReasoning}`, 
            'enthusiastic'
        );
    }

    setSessionComplete(true);
    setSessionState('complete');
    setStatusMessage('Perfect match found!');
        cleanupMicrophone();

    if (globalSocket && globalSocket.readyState === WebSocket.OPEN) {
        globalSocket.close();
    }
};

	const [hasMicrophonePermission, setHasMicrophonePermission] = useState(false);
useEffect(() => {
    const checkMicrophonePermission = async () => {
        try {
            const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
            if (permissionStatus.state === 'granted') {
                console.log('🎤 Microphone permission already granted');
                setHasMicrophonePermission(true);
            }
            
            // Listen for permission changes
            permissionStatus.onchange = () => {
                if (permissionStatus.state === 'granted') {
                    setHasMicrophonePermission(true);
                } else {
                    setHasMicrophonePermission(false);
                }
            };
        } catch (err) {
            // Permissions API might not be available in all browsers
            console.log('Could not check microphone permission status');
        }
    };
    
    checkMicrophonePermission();
}, []);




const [isPreparingSession, setIsPreparingSession] = useState(false);

const startSession = async () => {
    console.log('📞 startSession called');
    setCurrentQuestionReason('');
    
    // Show preparing state
    setIsPreparingSession(true);
    setSessionState('preparing'); // Add a new session state
    setStatusMessage('Preparing your session...');
    setIsVoiceSessionActive(true);
    
    try {
        // Pre-warm WebSocket connection
        const socket = getWebSocket();
        if (socket.readyState === WebSocket.CONNECTING) {
            console.log('⏳ Waiting for WebSocket to connect...');
            await new Promise((resolve) => {
                socket.onopen = () => {
                    console.log('✅ WebSocket connected');
                    resolve();
                };
            });
        }
        
        // Pre-initialize microphone with visual feedback
        setStatusMessage('Loading, please wait...');
        
        // Always try to pre-initialize for a fresh session
        try {
            console.log('🎤 Pre-initializing microphone for session...');
            const tempStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: { ideal: 24000 },
                    channelCount: { exact: 1 },
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            
            // Keep it ready for the upcoming listen
            preInitializedStreamRef.current = tempStream;
            
            // Pre-create audio context too
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
            if (audioCtx.state === 'suspended') {
                await audioCtx.resume();
            }
            preInitializedAudioContextRef.current = audioCtx;
            
            console.log('✅ Microphone ready for session');
            setHasMicrophonePermission(true);
            
            // Small delay for smooth transition
            await new Promise(resolve => setTimeout(resolve, 500));
        } catch (err) {
            console.error('❌ Failed to prepare microphone:', err);
            // Continue anyway, will request permission during listen
        }
        
        // Now ready to start the conversation
        setIsPreparingSession(false);
        setSessionState('ai-speaking');
        
        // const initialPrompt = "Hi, I'm Amy... and I'm here to help find the perfect therapist for you. So— what's on your mind?";
        const initialPrompt = "Hi";
        // Speak the initial prompt
        await speakWithVoiceAPI(initialPrompt, 'warm');
        
        // Start the loop
        await loopVoiceSession();
        
    } catch (error) {
        console.error('Failed to start session:', error);
        setIsPreparingSession(false);
        setSessionState('idle');
        setStatusMessage('Failed to start session. Please try again.');
    }
};

// const processNextQuestion = async (result, updatedHistory, currentInferredQuestions) => {
//     console.log('🔍 FRONTEND DEBUG - Processing next question');
    
//     // Get questions for analysis
//     const questionsForAnalysisResponse = await fetch('/api/natural-matchmaker/get-questions-for-analysis', {
//         method: 'POST',
//         headers: getAuthHeaders(),
//         body: JSON.stringify({
//             inferredQuestionIds: currentInferredQuestions,
//             previousQA: updatedHistory
//         })
//     });
    
//     const questionsData = await questionsForAnalysisResponse.json();
//     const questionsForAnalysis = questionsData.questions || [];
    
//     // Stage 1: Get therapeutic question (fast)
//     const therapeuticResponse = await fetch('/api/natural-matchmaker/get-therapeutic-question', {
//         method: 'POST',
//         headers: getAuthHeaders(),
//         body: JSON.stringify({
//             transcript: result.transcript,
//             tone: result.tone,
//             previousQA: updatedHistory,
//             inferredQuestionIds: currentInferredQuestions
//         })
//     });

//     if (!therapeuticResponse.ok) {
//         throw new Error(`HTTP error! status: ${therapeuticResponse.status}`);
//     }

//     const therapeuticData = await therapeuticResponse.json();
    
//     if (therapeuticData.finished) {
//         return { finished: true };
//     }

//     // Stage 2: Humanize the question (fast)
//     const humanizeResponse = await fetch('/api/natural-matchmaker/humanize-question', {
//         method: 'POST',
//         headers: getAuthHeaders(),
//         body: JSON.stringify({
//             question: therapeuticData.question,
//             tone: therapeuticData.tone,
//             emotionalContext: therapeuticData.emotionalContext,
//             intensityLevel: therapeuticData.intensityLevel,
//             conversationDepth: updatedHistory.length
//         })
//     });

//     const humanizeData = await humanizeResponse.json();
    
//     // Stage 3: Inference analysis (in parallel, but we don't wait for it)
//     const inferencePromise = fetch('/api/natural-matchmaker/analyze-inference', {
//         method: 'POST',
//         headers: getAuthHeaders(),
//         body: JSON.stringify({
//             transcript: result.transcript,
//             previousQA: updatedHistory,
//             inferredQuestionIds: currentInferredQuestions,
//             questionsForAnalysis: questionsForAnalysis
//         })
//     }).then(res => res.json());

//     // Return immediately with the humanized question
//     const fullResponseData = {
//         success: true,
//         questionId: null, // We're generating questions, not picking from a list
//         spokenText: humanizeData.spokenText,
//         tone: therapeuticData.tone,
//         reasonForQuestion: therapeuticData.reasonForQuestion,
//         // These will be updated when inference completes
//         inferredAnswers: [],
//         inferenceMapping: {},
//         inferencePromise: inferencePromise // Pass the promise so we can await it later
//     };

//     return fullResponseData;
// };
const loopVoiceSession = async (previousQuestionId = null, previousQuestion = null, currentHistory = qaHistory) => {
    // Check if we're stopping
    if (isSessionStoppingRef.current || isSessionStopping || sessionState === 'analyzing' || sessionState === 'complete') {
        console.log('🛑 Exiting voice loop - session stopped');
        return;
    }

    const historyToUse = currentHistory !== null ? currentHistory : qaHistory;

    let result;
    
    // Normal listening flow
    let retryCount = 0;
    const MAX_RETRIES = 3;
    
    // Keep trying to listen with retry limit
    while (retryCount < MAX_RETRIES) {
        // Check again before listening
        if (isSessionStoppingRef.current) {
            console.log('🛑 Session stopping, exiting before listen');
            return;
        }
        
        try {
            if (isSessionStoppingRef.current) return;

            result = await listenWithVoiceAPI();
			
            break;
        } catch (err) {
            console.error(`❌ Listen attempt ${retryCount + 1} failed:`, err);
            retryCount++;
            
            if (retryCount >= MAX_RETRIES || isSessionStoppingRef.current) {
                setStatusMessage('Session ended.');
                setSessionState('idle');
                return;
            }
            
            await new Promise(r => setTimeout(r, 1000));
        }
    }
    
    if (isSessionStoppingRef.current) return;

    // Update Q&A history if we have a previous question AND a transcript
     let updatedHistory = historyToUse;
    if (previousQuestionId && previousQuestion && result.transcript) {
        updatedHistory = [...historyToUse, {
            questionId: previousQuestionId,
            question: previousQuestion,
            answer: result.transcript
        }];
        setQaHistory(updatedHistory);
        
        // Update ref immediately
        qaHistoryRef.current = updatedHistory;
        
        if (localStorage.getItem('authToken')) {
            console.log('🔵 Force saving after Q&A update');
            await saveSessionState();
        }
    }
    // Check if session is complete
    if (result.sessionComplete) {
        console.log('🏁 Session complete from voice proxy');
        setSessionState('complete');
        return;
    }

    // Update inference data if we got any
    if (result.inference && result.inference.inferredAnswers && result.inference.inferredAnswers.length > 0) {
        console.log(`🧠 FRONTEND - AI inferred ${result.inference.inferredAnswers.length} new questions:`, result.inference.inferredAnswers);
        
        // Update inferred questions state
        await new Promise((resolve) => {
            setInferredQuestions(prev => {
                const updated = [...new Set([...prev, ...result.inference.inferredAnswers])];
                console.log(`📝 FRONTEND - Total inferred questions now: ${updated.length}`, updated);
                inferredQuestionsRef.current = updated; // Update ref too
                resolve();
                return updated;
            });
        });
        
        // Store the inferred answers
        const newInferredQAs = {};
        result.inference.inferredAnswers.forEach(questionId => {
            const specificReasoning = result.inference.inferenceMapping?.[questionId];
            const reasoning = specificReasoning || `Inferred from: "${result.transcript.substring(0, 100)}..."`;
            
            newInferredQAs[questionId] = {
                answer: `[Inferred] ${reasoning}`,
                fullTranscript: result.transcript,
                reasoning: reasoning
            };
        });
        
        // Update inferred Q&As state
        await new Promise((resolve) => {
            setInferredQAs(prev => {
                const updated = {...prev, ...newInferredQAs};
                inferredQAsRef.current = updated; // Update ref too
                resolve();
                return updated;
            });
        });
    }

    // Extract the next question from the therapeutic response
    const nextQuestionId = result.therapeuticResponse?.questionId || null;
    const nextQuestion = result.therapeuticResponse?.spokenText || '';
    
    // Update the current question reason for display
    if (result.therapeuticResponse?.reasonForQuestion) {
        setCurrentQuestionReason(result.therapeuticResponse.reasonForQuestion);
    }

    // Continue the loop only if we have a question to ask
    if (nextQuestion && !isSessionStoppingRef.current) {
        await loopVoiceSession(nextQuestionId, nextQuestion, updatedHistory);
    } else {
        console.log('⚠️ No next question received, ending loop');
        setSessionState('idle');
    }
};

	const [progressAnimating, setProgressAnimating] = useState(false);

	const finalizeMatch = async (finalQA) => {
		setLoading(true);
		setSessionState('analyzing');
		setStatusMessage('Finding the best therapist match for you...');

		const res = await fetch('/api/natural-matchmaker/finalize', {
			method: 'POST',
			headers: getAuthHeaders(),
			body: JSON.stringify({ previousQA: finalQA })
		});

		const data = await res.json();
		if (data.success) {
			setMatchResult(data.recommendation);
			await speakWithVoiceAPI(
				`I think ${data.recommendation.name} would be a wonderful fit. ${data.recommendation.matchReasoning}`,
				'warm'
			);   	
		}

		setLoading(false);
		setSessionState('complete');
		setStatusMessage('Your ideal match is ready!');
		
		if (globalSocket && globalSocket.readyState === WebSocket.OPEN) {
			globalSocket.close();
		}
	};

	// Enhanced status messages based on session state
const getStatusMessage = () => {
    switch (sessionState) {
        case 'preparing':
            return isPreparingSession ? "Setting up audio..." : "Getting ready...";
        case 'ai-speaking':
            return "I'm sharing my thoughts...";
        case 'listening':
            return "I'm listening—feel free to share openly.";
        case 'analyzing':
            return "Let me think about what you've said...";
        case 'complete':
            return "We've found the perfect therapist for you!";
        case 'processing':
            return "Processing what you said...";
        default:
            return "I'm ready whenever you are.";
    }
};

	
	  if (checkingAccess) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 flex items-center justify-center">
                <Loader className="animate-spin text-white" size={48} />
            </div>
        );
    }

    // Show invite code entry if no access - THIS MUST COME BEFORE SESSION CHECKS
    if (!hasAccess) {
        return <InviteCodeEntry onValidCode={() => {
            setHasAccess(true);
        }} />;
    }


	return (
		<div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 flex flex-col">
			<header className="bg-white bg-opacity-10 backdrop-blur-lg shadow-lg border-b border-white border-opacity-20">
				<div className="container mx-auto flex items-center justify-between p-4">
				<div className="flex items-center">
					<img 
						src="/images/mindaera_logo.png" 
						alt="Mindaera" 
						className="h-10 w-auto brightness-0 invert mr-3"
					/>
					{/* <div>
						<p className="text-sm text-blue-100">Your AI therapy matchmaker</p>
					</div> */}
				</div>
					{localStorage.getItem('authToken') && (
						<button 
							onClick={() => window.location.href = '/client-profile'}
							className="text-sm text-white hover:text-blue-200 transition-colors bg-white bg-opacity-10 backdrop-blur-lg px-4 py-2 rounded-full hover:bg-opacity-20"
						>
							My Profile
						</button>
					)}
				</div>
			</header>

			<main className="container mx-auto p-6 flex-grow flex items-center justify-center">
				{sessionState === 'idle' && (
  <>
    {/* Case 1: Checking sessions (logged in users only) */}
    {isCheckingSessions && localStorage.getItem('authToken') && (
      <div className="text-center">
        <Loader className="animate-spin mx-auto mb-4 text-white" size={32} />
        <p className="text-white">Loading your session...</p>
      </div>
    )}
    
    {/* Case 2: Logged in with active session */}
    {!isCheckingSessions && showSessionChoice && localStorage.getItem('authToken') && activeSessions.length > 0 && (
      <div className="text-center max-w-md">
        <div className="mb-8">
          <div className="w-32 h-32 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Sparkles className="text-white" size={48} />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Welcome Back!</h2>
          <p className="text-xl text-blue-100 mb-8">
            Continue your conversation with Amy?
          </p>
        </div>
        
        <div className="space-y-4">
          <button
            onClick={() => continueSession(activeSessions[0].sessionId)}
            disabled={isLoadingSession}
            className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold text-lg rounded-full hover:shadow-lg transition-all transform hover:scale-105 shadow-xl"
          >
            {isLoadingSession ? (
              <Loader className="inline animate-spin mr-2" size={20} />
            ) : (
              <MessageCircle className="inline mr-2" size={20} />
            )}
            Continue Conversation
          </button>
          
          <button
            onClick={startNewSession}
            className="w-full px-8 py-4 bg-white bg-opacity-20 backdrop-blur-lg text-white font-semibold text-lg rounded-full hover:bg-opacity-30 transition-all transform hover:scale-105 shadow-xl border border-white border-opacity-20"
          >
            Start Fresh
          </button>
        </div>
      </div>
    )}
    
    {/* Case 3: No active session (logged in or not) */}
    {!isCheckingSessions && !showSessionChoice && (
      <div className="text-center max-w-md">
        <div className="mb-8">
          <div className="w-32 h-32 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl">
            <Sparkles className="text-white" size={48} />
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Welcome!</h2>
          <p className="text-xl text-blue-100 mb-8">
            Ready to find your perfect therapist match?
          </p>
        </div>
        <button
          onClick={startSession}
          className="px-10 py-5 bg-white bg-opacity-20 backdrop-blur-lg text-white font-bold text-lg rounded-full hover:bg-opacity-30 transition-all transform hover:scale-105 shadow-xl border border-white border-opacity-20"
        >
          Start Conversation <ArrowRight className="inline-block ml-2" />
        </button>
      </div>
    )}
  </>
)}

				{sessionState !== 'idle' && sessionState !== 'complete' && (
					 <div className="max-w-2xl mx-auto text-center">
        {/* Main Circular Voice Visualizer */}
        <div className="mb-8">
            <CircularVoiceVisualizer 
                userLevels={userAudioLevels}
                aiLevels={aiAudioLevels}
                isUserActive={sessionState === 'listening'}
                isAiActive={sessionState === 'ai-speaking'}
                sessionState={sessionState}
            />
        </div>

						{/* Status Message with Reasoning Tooltip */}
						<div className="mb-6">
						<p className="text-2xl text-white font-medium">
							{statusMessage}
						</p>
						{currentQuestionReason && (
							<div className="mt-2 max-w-md mx-auto">
							<div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white border-opacity-20">
								<p className="text-sm text-blue-100 italic">
								<span className="text-xs opacity-75">Question Reasoning: </span>
								{currentQuestionReason}
								</p>
							</div>
							</div>
						)}
						</div>
						{/* Match Button - Shows progressively */}
						{sessionState !== 'analyzing' && (
							<div className="mb-6">
								<button
									onClick={handleTriggerMatch}
									onMouseEnter={() => setIsMatchButtonHovered(true)}
									onMouseLeave={() => setIsMatchButtonHovered(false)}
									disabled={!canTriggerMatch || isSessionStopping}
									className={`
										px-10 py-5 rounded-full font-bold text-lg
										transition-all duration-300 transform
										${canTriggerMatch && !isSessionStopping
											? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:scale-105 shadow-2xl cursor-pointer border border-white border-opacity-20' 
											: 'bg-white bg-opacity-10 backdrop-blur-lg text-white text-opacity-50 cursor-not-allowed border border-white border-opacity-10'
										}
									`}
									style={{
										opacity: matchButtonOpacity,
										filter: canTriggerMatch ? 'none' : 'grayscale(50%)'
									}}
								>
									{/* Add pulsing dot indicator when ready */}
									{canTriggerMatch && !isSessionStopping && (
										<span className="inline-flex items-center">
											<span className="animate-pulse inline-block w-2 h-2 bg-white rounded-full mr-2"></span>
											✨ Find My Therapist
										</span>
									)}
									{isSessionStopping && '⏳ Processing...'}
									{!canTriggerMatch && '🔒 Keep Talking...'}
								</button>
								
								{/* Progress indicator */}
								<div className="mt-3 text-white text-opacity-90">
									{(() => {
										const actualCount = Object.keys(inferredQAs).length;
										const displayCount =  actualCount;
										const percentage = Math.round((displayCount / 7) * 100);
										
										return displayCount < 5 ? (
											<span className="flex items-center justify-center">
												<span className="inline-block mr-2">Understanding you better...</span>
												<span className={`font-bold text-purple-200 transition-all duration-300 ${
													progressAnimating ? 'scale-125 text-white' : 'scale-100'
												}`}>
												({percentage}%)
												</span>
											</span>
										) : (
											<div className="space-y-1">
												<span className="text-green-300 font-medium flex items-center justify-center">
													<svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
														<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
													</svg>
													{hasAnnouncedReady ? 
														"Ready when you are!" :
														"Gathering final insights..."
													}
												</span>
												<span className="text-xs text-blue-200 opacity-80">
													Continue sharing or click to find your match
												</span>
											</div>
										);
									})()}
								</div>
								
								
								{/* Additional hint when ready with Tailwind animation */}
								{canTriggerMatch && !isSessionStopping && (
									<div className="mt-4 relative">
										<div className="absolute inset-0 flex items-center" aria-hidden="true">
											<div className="w-full border-t border-white border-opacity-20"></div>
										</div>
										<div className="relative flex justify-center">
											<span className="px-3 bg-gradient-to-br from-blue-600 via-purple-600 to-blue-700 text-xs text-purple-200 font-medium">
												💡 The more you share, the better your match!
											</span>
										</div>
									</div>
								)}
								
								{/* Visual progress bar */}
								<div className="mt-4 w-full bg-white bg-opacity-10 backdrop-blur-lg rounded-full h-3 overflow-hidden border border-white border-opacity-20">
									<div 
										className="bg-gradient-to-r from-purple-400 to-blue-400 h-full rounded-full transition-all duration-500 ease-out relative"
										style={{ 
											width: `${(() => {
												const actualCount = Object.keys(inferredQAs).length;
												const displayCount = actualCount;
												return Math.min(100, (displayCount / 7) * 100);
											})()}%` 
										}}
									>
										{/* Shimmer effect for progress bar */}
										{canTriggerMatch && (
											<div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
										)}
									</div>
								</div>
							</div>
						)}

						{/* Helpful Tips */}
						<div className="text-center text-blue-100">
							<p className="text-sm">
								{sessionState === 'listening' && "Take your time - speak naturally"}
								{sessionState === 'ai-speaking' && "Amy is sharing insights with you"}
								{sessionState === 'analyzing' && "Processing your unique needs..."}
							</p>
						</div>
					</div>
				)}

				{
					sessionState === 'complete' && matchResult && (
						<div className="bg-white rounded-3xl p-10 shadow-2xl max-w-2xl mx-auto">
							<div className="text-center mb-8">
								<div className="w-20 h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
									<svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
										<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
									</svg>
								</div>
								<h2 className="text-3xl font-bold text-gray-800 mb-2">Perfect Match Found!</h2>
								<p className="text-gray-600">We've found the ideal therapist for your unique needs</p>
							</div>

							<div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 mb-6">
								<h3 className="text-2xl font-bold text-purple-800 mb-3">{matchResult.name}</h3>
								<div className="flex flex-wrap gap-3 mb-6">
									<span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
										{matchResult.specialty}
									</span>
									<span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm">
										{matchResult.experience}
									</span>
								</div>
								<p className="text-gray-700 mb-6 leading-relaxed text-lg">{matchResult.style}</p>
							</div>

							<div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200 mb-8">
								<h4 className="font-bold text-green-800 mb-3 text-lg flex items-center">
									<Heart className="w-5 h-5 mr-2 text-green-600" />
									Why You'll Work Well Together
								</h4>
								<p className="text-green-700 leading-relaxed">
									{matchResult.matchReasoning}
								</p>
							</div>

							<div className="flex flex-col sm:flex-row gap-4 justify-center">
								<button
onClick={() => {
        // Clean up before reload
        cleanupMicrophone();
        window.location.reload();
    }}									className="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-lg text-purple-600 font-semibold rounded-full hover:bg-opacity-30 transition-all border-2 border-purple-600"
								>
									Try Another Match
								</button>
								<button className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg transition-all">
									Book First Session <ArrowRight className="inline-block ml-2" />
								</button>
							</div>
						</div>
					)
				}
			</main>
		</div>
	);
};

export default function WrappedNaturalMatchmakerPage() {
	return (
		<VoiceErrorBoundary>
			<NaturalMatchmakerPage />
		</VoiceErrorBoundary>
	);
}